import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatDisplayDate, formatDisplayDateTime, formatRelativeTime } from "../../utils/dateFormat";
import { 
  Calendar, RefreshCw, Save, Plus, Trash2, Settings, 
  Link2, DollarSign, Clock, Users, AlertCircle, CheckCircle, Check,
  Download, Upload, Database, Copy, ChevronDown, History, ExternalLink, RotateCcw, Shield, Mail
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import PriceCalendarGrid from "./PriceCalendarGrid";
import BookingsCalendar from "./BookingsCalendar";
import { PageInfoUnderTabs, PageInfoIcon } from "./PageInfo";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import ConfirmModal from "../ui/ConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform Rate Calculator Component - Collapsible accordion
function AirbnbRateCalculator({ airbnbRateReduction, onReductionChange }) {
  const [airbnbRate, setAirbnbRate] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  
  // Calculate reduced rate
  const reducedRate = airbnbRate && !isNaN(parseFloat(airbnbRate)) 
    ? (parseFloat(airbnbRate) * (1 - airbnbRateReduction / 100)).toFixed(2)
    : "";

  return (
    <div className="admin-card border-l-4 border-l-[#D4AF37] bg-gradient-to-r from-[#FFFDF7] to-white overflow-hidden">
      {/* Accordion Header - Clickable */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between cursor-pointer hover:bg-[#FFFDF7] transition-colors -m-4 p-4 rounded-lg"
        aria-expanded={isOpen}
        aria-controls="rate-calculator-content"
      >
        <div className="flex items-center gap-2">
          <DollarSign size={20} className="text-[#D4AF37]" />
          <h3 className="font-heading text-lg font-semibold text-[#1C1917] text-left">
            Calcola la tariffa ridotta in % partendo dai prezzi delle piattaforme
          </h3>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-[#78716C] transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Accordion Content - Animated */}
      <div
        id="rate-calculator-content"
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <p className="text-xs text-[#78716C] mb-4">
            Calcolatrice per garantire il miglior prezzo sul sito rispetto alle piattaforme (Airbnb, Booking...).
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Riduzione percentuale - Persistente */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#44403C]">Riduzione percentuale (%)</label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  step="0.1"
                  min="0"
                  max="100"
                  value={airbnbRateReduction} 
                  onChange={(e) => onReductionChange(parseFloat(e.target.value) || 0)} 
                  className="w-24"
                />
                <span className="text-[#78716C]">%</span>
              </div>
              <p className="text-xs text-[#78716C]">Persistente (salvato)</p>
            </div>

            {/* Tariffa da piattaforma - Input manuale */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#44403C]">Tariffa da piattaforma (€)</label>
              <div className="flex items-center gap-2">
                <span className="text-[#78716C]">€</span>
                <Input 
                  type="number" 
                  step="1"
                  min="0"
                  placeholder="es. 120"
                  value={airbnbRate} 
                  onChange={(e) => setAirbnbRate(e.target.value)} 
                  className="w-28"
                />
              </div>
              <p className="text-xs text-[#78716C]">Inserisci manualmente</p>
            </div>

            {/* Tariffa ridotta - Output */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#44403C]">Tariffa ridotta (€)</label>
              <div className="flex items-center gap-2 p-2 bg-[#FFFDF7] border border-[#D4AF37] rounded-lg min-h-[40px]">
                <span className="text-[#78716C]">€</span>
                <span className={`text-lg font-bold ${reducedRate ? 'text-[#D4AF37]' : 'text-[#A8A29E]'}`}>
                  {reducedRate || "—"}
                </span>
              </div>
              <p className="text-xs text-[#78716C]">Calcolato automaticamente</p>
            </div>
          </div>

          {/* Formula */}
          {airbnbRate && reducedRate && (
            <div className="mt-4 p-3 bg-white rounded-lg border border-[#E7E5E4]">
              <p className="text-sm">
                <span className="text-[#78716C]">Formula:</span>{" "}
                <span className="font-medium">€{airbnbRate} × (1 - {airbnbRateReduction}%) = </span>
                <span className="font-bold text-[#D4AF37]">€{reducedRate}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Calendar Preview Component
function CalendarPreview({ prices, config }) {
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendar();
  }, []);

  const fetchCalendar = async () => {
    try {
      const response = await axios.get(`${API}/calendar?months=3`);
      setCalendarData(response.data.calendar || []);
    } catch (error) {
      console.error("Failed to fetch calendar preview");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-center"><RefreshCw className="animate-spin mx-auto" /></div>;

  // Group by month
  const byMonth = {};
  calendarData.forEach(day => {
    const month = day.date.substring(0, 7);
    if (!byMonth[month]) byMonth[month] = [];
    byMonth[month].push(day);
  });

  return (
    <div className="space-y-6 mt-4">
      {Object.entries(byMonth).map(([month, days]) => (
        <div key={month}>
          <h4 className="font-medium mb-3">{month}</h4>
          <div className="grid grid-cols-7 gap-1 text-xs">
            {["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"].map(d => (
              <div key={d} className="text-center text-[#78716C] py-1">{d}</div>
            ))}
            {/* Add padding for first day */}
            {Array(new Date(days[0].date + "T00:00:00").getDay()).fill(null).map((_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {days.map(day => {
              // Determine cell style based on block source
              let cellClass = "p-1 text-center rounded ";
              let label = "";
              
              if (!day.available) {
                if (day.block_source === "ical") {
                  // iCal (Airbnb/Booking) - Gray like main calendar
                  cellClass += "bg-gray-200 text-gray-600";
                  label = "iCal";
                } else if (day.block_source === "booking") {
                  // Actual bookings - Cyan/Teal
                  cellClass += "bg-cyan-100 text-cyan-700";
                  label = "Prenotato";
                } else if (day.block_source === "manual") {
                  // Manual blocks - Red
                  cellClass += "bg-red-100 text-red-600";
                  label = "Bloccato";
                } else {
                  // Other blocks
                  cellClass += "bg-red-100 text-red-600";
                  label = "Bloccato";
                }
              } else if (day.price) {
                cellClass += "bg-green-50";
              } else {
                cellClass += "bg-gray-50";
              }
              
              return (
                <div key={day.date} className={cellClass}>
                  <div className="font-medium">{new Date(day.date + "T00:00:00").getDate()}</div>
                  {day.available && day.price && <div className="text-[#D4AF37]">€{day.price}</div>}
                  {!day.available && <div className="text-[10px] truncate">{label}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-3 text-xs text-[#78716C] mt-4">
        <div className="flex items-center gap-1"><span className="w-3 h-3 bg-green-50 rounded border border-green-200" /> Disponibile</div>
        <div className="flex items-center gap-1"><span className="w-3 h-3 bg-cyan-100 rounded" /> Prenotato</div>
        <div className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-200 rounded" /> Airbnb/Booking</div>
        <div className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 rounded" /> Bloccato</div>
      </div>
    </div>
  );
}

export default function CalendarManagement({ getAuthHeaders, syncErrorsCount = 0, markAsVisited }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Leggi il tab dall'URL se presente
    const tabParam = searchParams.get('tab');
    return tabParam && ['booking', 'source', 'sync', 'settings'].includes(tabParam) ? tabParam : 'booking';
  });
  const [config, setConfig] = useState(null);
  const [prices, setPrices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showAddPrice, setShowAddPrice] = useState(false);
  const [selectedPrices, setSelectedPrices] = useState([]);
  const [deletePriceDialogOpen, setDeletePriceDialogOpen] = useState(false);
  const [deletingPriceId, setDeletingPriceId] = useState(null);
  const [deletingPriceBulk, setDeletingPriceBulk] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  
  // Multi-platform iCal state
  const [platforms, setPlatforms] = useState([]);
  const [showAddPlatform, setShowAddPlatform] = useState(false);
  const [newPlatform, setNewPlatform] = useState({
    platform_name: "",
    ical_url: "",
    sync_interval_minutes: 60,
    enabled: true
  });
  const [syncingPlatform, setSyncingPlatform] = useState(null);
  const [deletePlatformDialogOpen, setDeletePlatformDialogOpen] = useState(false);
  const [deletingPlatform, setDeletingPlatform] = useState(null);
  
  // Stato condiviso per l'anno corrente (navigazione per anno)
  const today = new Date();
  const [sharedYear, setSharedYear] = useState(today.getFullYear());
  const [newPrice, setNewPrice] = useState({
    start_date: "", end_date: "", price_per_night: "", min_nights: "", notes: ""
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [blocked, setBlocked] = useState([]);
  const [overbookingDates, setOverbookingDates] = useState([]);
  
  // Sync Logs state
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncOverview, setSyncOverview] = useState(null);
  const [loadingSyncLogs, setLoadingSyncLogs] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [expandedBookingsList, setExpandedBookingsList] = useState(null); // Track which log has expanded bookings list
  // Imported bookings stats for 5 cards
  const [importedBookingsStats, setImportedBookingsStats] = useState(null);
  const [expandedCardBookings, setExpandedCardBookings] = useState(null); // Which card's bookings list is expanded
  
  // Sync filters
  const [syncFilterPlatform, setSyncFilterPlatform] = useState("all");
  const [syncFilterType, setSyncFilterType] = useState("all");
  const [syncFilterPeriod, setSyncFilterPeriod] = useState("all");
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  // Sync selection for bulk delete
  const [selectedSyncLogs, setSelectedSyncLogs] = useState([]);
  const [selectedBookingLogs, setSelectedBookingLogs] = useState([]); // Per eliminazione prenotazioni rilevate
  
  // Stripe Credentials state
  const [stripeCredentials, setStripeCredentials] = useState(null);
  const [stripeTestPublishable, setStripeTestPublishable] = useState("");
  const [stripeTestSecret, setStripeTestSecret] = useState("");
  const [stripeTestWebhook, setStripeTestWebhook] = useState("");
  const [stripeLivePublishable, setStripeLivePublishable] = useState("");
  const [stripeLiveSecret, setStripeLiveSecret] = useState("");
  const [stripeLiveWebhook, setStripeLiveWebhook] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalType, setDeleteModalType] = useState("single"); // "single" or "bulk"
  const [logToDelete, setLogToDelete] = useState(null);
  // Business events vs technical logs toggle
  const [showBusinessEventsOnly, setShowBusinessEventsOnly] = useState(true); // Default: show only business events
  // Delete ALL logs modal - ora con modale Sì/No semplice
  const [showDeleteAllLogsModal, setShowDeleteAllLogsModal] = useState(false);
  const [deletingAllLogs, setDeletingAllLogs] = useState(false);
  // Nuovo modale Sì/No semplice per eliminazioni
  const [showSimpleDeleteModal, setShowSimpleDeleteModal] = useState(false);
  const [deleteTargetType, setDeleteTargetType] = useState(""); // "bookings", "logs", "all"
  
  // Public Base URL state (iCal Export)
  const [publicBaseUrlConfig, setPublicBaseUrlConfig] = useState(null);
  const [publicBaseUrlInput, setPublicBaseUrlInput] = useState("");
  const [publicBaseUrlPassword, setPublicBaseUrlPassword] = useState("");
  const [savingPublicBaseUrl, setSavingPublicBaseUrl] = useState(false);
  const [testingIcalUrl, setTestingIcalUrl] = useState(false);
  const [icalTestResult, setIcalTestResult] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Confirmation modals state (non-destructive)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: "",
    message: "",
    variant: "question",
    onConfirm: () => {}
  });
  
  // Delete booking modal (from calendar)
  const [deleteBookingModalOpen, setDeleteBookingModalOpen] = useState(false);
  const [bookingsToDelete, setBookingsToDelete] = useState([]);
  
  // Track initial load to prevent auto-save on first render
  const isInitialLoad = useRef(true);
  const saveTimeoutRef = useRef(null);
  
  // Auto-save config when it changes (debounced)
  const autoSaveConfig = useCallback(async (configToSave) => {
    if (!configToSave || isInitialLoad.current) return;
    
    try {
      await axios.put(`${API}/admin/calendar/config`, configToSave, getAuthHeaders());
      
      // Update scheduler interval if changed
      if (configToSave.sync_interval_minutes) {
        await axios.post(`${API}/admin/calendar/sync-interval?interval=${configToSave.sync_interval_minutes}`, {}, getAuthHeaders());
      }
      
      toast.success("Impostazioni salvate automaticamente", { duration: 2000 });
    } catch (error) {
      toast.error("Errore nel salvataggio automatico");
    }
  }, [getAuthHeaders]);
  
  // Debounced auto-save effect
  useEffect(() => {
    if (isInitialLoad.current) {
      return;
    }
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout for debounced save (1.5 seconds after last change)
    saveTimeoutRef.current = setTimeout(() => {
      autoSaveConfig(config);
    }, 1500);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [config, autoSaveConfig]);
  
  // Handle overbooking resolution - navigate to Payments > Overbooking tab
  const handleResolveOverbooking = (dates) => {
    toast.info(`Gestione overbooking per ${dates.length} date in conflitto`);
    navigate("/admin/dashboard/payments?tab=overbookings");
  };
  
  // Stripe Credentials functions
  const fetchStripeCredentials = async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe/credentials`, getAuthHeaders());
      setStripeCredentials(res.data);
    } catch (error) {
      console.error("Failed to fetch Stripe credentials:", error);
    }
  };
  
  const saveStripeCredentials = async () => {
    // Show confirmation for secrets
    const hasSecrets = stripeTestSecret || stripeLiveSecret || stripeTestWebhook || stripeLiveWebhook;
    if (hasSecrets) {
      setConfirmModalConfig({
        title: "Salvataggio Credenziali",
        message: "Le Secret Key e i Webhook Secret non saranno più visualizzabili dopo il salvataggio. Si consiglia di salvare le credenziali in una cartella sicura.",
        variant: "warning",
        onConfirm: () => {
          setConfirmModalOpen(false);
          doSaveStripeCredentials();
        }
      });
      setConfirmModalOpen(true);
      return;
    }
    
    doSaveStripeCredentials();
  };
  
  const doSaveStripeCredentials = async () => {
    try {
      const payload = {};
      if (stripeTestPublishable) payload.publishable_key_test = stripeTestPublishable;
      if (stripeTestSecret) payload.secret_key_test = stripeTestSecret;
      if (stripeTestWebhook) payload.webhook_secret_test = stripeTestWebhook;
      if (stripeLivePublishable) payload.publishable_key_live = stripeLivePublishable;
      if (stripeLiveSecret) payload.secret_key_live = stripeLiveSecret;
      if (stripeLiveWebhook) payload.webhook_secret_live = stripeLiveWebhook;
      
      await axios.put(`${API}/admin/stripe/credentials`, payload, getAuthHeaders());
      toast.success("Credenziali Stripe salvate");
      
      // Clear input fields
      setStripeTestPublishable("");
      setStripeTestSecret("");
      setStripeTestWebhook("");
      setStripeLivePublishable("");
      setStripeLiveSecret("");
      setStripeLiveWebhook("");
      
      // Refresh credentials
      fetchStripeCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };
  
  // Filter bookings by status
  const filteredBookings = statusFilter === "all" 
    ? bookings 
    : bookings.filter(b => b.status === statusFilter);

  // Fetch sync status periodically
  const fetchSyncStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/calendar/sync-status`, getAuthHeaders());
      setSyncStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch sync status");
    }
  };

  // Fetch iCal platforms
  const fetchPlatforms = async () => {
    try {
      const res = await axios.get(`${API}/admin/calendar/platforms`, getAuthHeaders());
      setPlatforms(res.data || []);
    } catch (error) {
      console.error("Failed to fetch platforms:", error);
    }
  };

  // Add new platform
  const addPlatform = async () => {
    if (!newPlatform.platform_name || !newPlatform.ical_url) {
      toast.error("Nome piattaforma e URL iCal sono obbligatori");
      return;
    }
    try {
      await axios.post(`${API}/admin/calendar/platforms`, newPlatform, getAuthHeaders());
      toast.success(`Piattaforma "${newPlatform.platform_name}" aggiunta`);
      setShowAddPlatform(false);
      setNewPlatform({ platform_name: "", ical_url: "", sync_interval_minutes: 60, enabled: true });
      fetchPlatforms();
    } catch (error) {
      toast.error("Errore nell'aggiunta della piattaforma");
    }
  };

  // Update platform
  const updatePlatform = async (platform) => {
    try {
      await axios.put(`${API}/admin/calendar/platforms/${platform.platform_id}`, platform, getAuthHeaders());
      toast.success(`Piattaforma "${platform.platform_name}" aggiornata`);
      fetchPlatforms();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Delete platform
  const deletePlatform = async (platform) => {
    setDeletingPlatform(platform);
    setDeletePlatformDialogOpen(true);
  };
  
  const confirmDeletePlatform = async () => {
    const platform = deletingPlatform;
    try {
      await axios.delete(`${API}/admin/calendar/platforms/${platform.platform_id}`, getAuthHeaders());
      toast.success(`Piattaforma "${platform.platform_name}" eliminata`);
      fetchPlatforms();
      fetchData(); // Refresh blocked dates
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeletePlatformDialogOpen(false);
      setDeletingPlatform(null);
    }
  };

  // Fetch sync logs and overview with filters
  const fetchSyncLogs = async (filters = {}) => {
    setLoadingSyncLogs(true);
    try {
      // Build query params - NON usiamo events_only per caricare TUTTI i log
      // Il filtraggio tra "Nuove Prenotazioni" e "Log Errori" avviene lato client
      const params = new URLSearchParams({ limit: "100" });
      const platformFilter = filters.platform || syncFilterPlatform;
      const periodFilter = filters.period || syncFilterPeriod;
      
      if (platformFilter && platformFilter !== "all") params.append("platform_name", platformFilter);
      if (periodFilter && periodFilter !== "all") params.append("period", periodFilter);
      // NON aggiungiamo events_only - vogliamo TUTTI i log per mostrarli nelle due sezioni
      
      const [logsRes, overviewRes, platformsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/sync-logs?${params.toString()}`, getAuthHeaders()),
        axios.get(`${API}/admin/sync-status`, getAuthHeaders()),
        axios.get(`${API}/admin/sync-logs/platforms`, getAuthHeaders()),
        axios.get(`${API}/admin/sync-logs/imported-bookings-stats`, getAuthHeaders())
      ]);
      setSyncLogs(logsRes.data || []);
      setSyncOverview(overviewRes.data || null);
      setAvailablePlatforms(platformsRes.data || []);
      setImportedBookingsStats(statsRes.data || null);
      setSelectedSyncLogs([]); // Clear selection when filters change
      setSelectedBookingLogs([]); // Clear booking selection too
    } catch (error) {
      console.error("Failed to fetch sync logs:", error);
    } finally {
      setLoadingSyncLogs(false);
    }
  };

  // Delete single sync log
  const confirmDeleteSyncLog = (logId) => {
    setLogToDelete(logId);
    setDeleteModalType("single");
    setShowDeleteModal(true);
  };

  // Delete bulk sync logs
  const confirmDeleteSyncLogsBulk = () => {
    if (selectedSyncLogs.length === 0) {
      toast.error("Seleziona almeno un log da eliminare");
      return;
    }
    setDeleteModalType("bulk");
    setShowDeleteModal(true);
  };

  // Execute deletion
  const executeDelete = async () => {
    try {
      if (deleteModalType === "single" && logToDelete) {
        await axios.delete(`${API}/admin/sync-logs/${logToDelete}`, getAuthHeaders());
        toast.success("Log eliminato definitivamente");
      } else if (deleteModalType === "bulk") {
        await axios.delete(`${API}/admin/sync-logs/bulk`, {
          ...getAuthHeaders(),
          data: { ids: selectedSyncLogs }
        });
        toast.success(`${selectedSyncLogs.length} log eliminati definitivamente`);
        setSelectedSyncLogs([]);
      }
      setShowDeleteModal(false);
      setLogToDelete(null);
      fetchSyncLogs();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Delete ALL sync logs - ora con modale Sì/No
  const executeDeleteAllLogs = async () => {
    setDeletingAllLogs(true);
    try {
      const response = await axios.delete(`${API}/admin/sync-logs/all`, getAuthHeaders());
      toast.success(response.data.message || "Log eliminati con successo");
      setShowDeleteAllLogsModal(false);
      setShowSimpleDeleteModal(false);
      setSelectedSyncLogs([]);
      setSelectedBookingLogs([]);
      fetchSyncLogs(); // Refresh the list (will be empty)
    } catch (error) {
      toast.error("Errore durante l'eliminazione: " + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingAllLogs(false);
    }
  };

  // Esegui eliminazione dal modale semplice Sì/No
  const executeSimpleDelete = async () => {
    try {
      if (deleteTargetType === "bookings" && selectedBookingLogs.length > 0) {
        // Elimina prenotazioni rilevate selezionate
        await axios.delete(`${API}/admin/sync-logs/bulk`, {
          ...getAuthHeaders(),
          data: { ids: selectedBookingLogs }
        });
        toast.success(`${selectedBookingLogs.length} prenotazioni eliminate`);
        setSelectedBookingLogs([]);
      } else if (deleteTargetType === "logs" && selectedSyncLogs.length > 0) {
        // Elimina log tecnici selezionati
        await axios.delete(`${API}/admin/sync-logs/bulk`, {
          ...getAuthHeaders(),
          data: { ids: selectedSyncLogs }
        });
        toast.success(`${selectedSyncLogs.length} log eliminati`);
        setSelectedSyncLogs([]);
      } else if (deleteTargetType === "all_bookings") {
        // Elimina TUTTE le prenotazioni rilevate
        const bookingLogIds = syncLogs
          .filter(log => log.new_bookings_count > 0 || log.delta > 0)
          .map(log => log.log_id);
        if (bookingLogIds.length > 0) {
          await axios.delete(`${API}/admin/sync-logs/bulk`, {
            ...getAuthHeaders(),
            data: { ids: bookingLogIds }
          });
          toast.success(`${bookingLogIds.length} prenotazioni eliminate`);
        }
        setSelectedBookingLogs([]);
      } else if (deleteTargetType === "all_logs") {
        // Elimina TUTTI i log di errore
        const errorLogIds = syncLogs
          .filter(log => log.status === 'error')
          .map(log => log.log_id);
        if (errorLogIds.length > 0) {
          await axios.delete(`${API}/admin/sync-logs/bulk`, {
            ...getAuthHeaders(),
            data: { ids: errorLogIds }
          });
          toast.success(`${errorLogIds.length} log eliminati`);
        }
        setSelectedSyncLogs([]);
      } else if (deleteTargetType === "all") {
        // Elimina tutti i log (legacy - non più usato)
        await executeDeleteAllLogs();
        return; // executeDeleteAllLogs gestisce già il modale
      } else if (deleteTargetType === "single" && logToDelete) {
        // Elimina singolo log
        await axios.delete(`${API}/admin/sync-logs/${logToDelete}`, getAuthHeaders());
        toast.success("Log eliminato");
        setLogToDelete(null);
      }
      setShowSimpleDeleteModal(false);
      setDeleteTargetType("");
      fetchSyncLogs();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Toggle select all (only visible/filtered logs)
  const toggleSelectAllSyncLogs = () => {
    if (selectedSyncLogs.length === syncLogs.length) {
      setSelectedSyncLogs([]);
    } else {
      setSelectedSyncLogs(syncLogs.map(l => l.log_id));
    }
  };

  // Sync single platform
  const syncPlatform = async (platform) => {
    setSyncingPlatform(platform.platform_id);
    try {
      const res = await axios.post(`${API}/admin/calendar/platforms/${platform.platform_id}/sync`, {}, getAuthHeaders());
      const msg = res.data.new_bookings_count > 0 
        ? `Sincronizzazione completata: ${res.data.new_bookings_count} nuove prenotazioni` 
        : `Sincronizzazione completata: nessuna nuova prenotazione`;
      toast.success(msg);
      fetchPlatforms();
      fetchData(); // Refresh blocked dates
      fetchSyncLogs(); // Refresh sync logs
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella sincronizzazione");
    } finally {
      setSyncingPlatform(null);
    }
  };
  
  // Smart polling: traccia la versione del calendario per evitare fetch inutili
  const [lastKnownVersion, setLastKnownVersion] = useState(0);

  useEffect(() => {
    fetchData(true); // Solo il caricamento iniziale mostra loading spinner
    fetchSyncStatus();
    fetchPlatforms();
    fetchStripeCredentials(); // Load Stripe credentials
    // Refresh sync status every minute
    const interval = setInterval(fetchSyncStatus, 60000);
    return () => clearInterval(interval);
  }, []);
  
  // Smart polling: controlla versione ogni 60 secondi (solo se tab visibile)
  useEffect(() => {
    const checkCalendarVersion = async () => {
      // Controlla solo se la tab è visibile
      if (document.hidden) return;
      
      try {
        const res = await axios.get(`${API}/calendar/version`, getAuthHeaders());
        const serverVersion = res.data.version;
        
        if (serverVersion > lastKnownVersion && lastKnownVersion > 0) {
          console.log(`📊 Booking Calendar: Calendar version changed (${lastKnownVersion} → ${serverVersion}), refreshing data...`);
          fetchData(false); // Refresh silenzioso senza loading spinner
        }
        setLastKnownVersion(serverVersion);
      } catch (error) {
        console.error("Error checking calendar version:", error);
      }
    };
    
    // Polling ogni 60 secondi
    const interval = setInterval(checkCalendarVersion, 60000);
    
    // Carica versione iniziale
    checkCalendarVersion();
    
    // Ricontrolla quando la tab diventa visibile
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkCalendarVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastKnownVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (isInitial = false) => {
    // Solo il caricamento iniziale mostra lo spinner (evita smontaggio calendari)
    if (isInitial) setLoading(true);
    try {
      const [configRes, pricesRes, bookingsRes, blockedRes, overbookingRes, publicBaseUrlRes] = await Promise.all([
        axios.get(`${API}/admin/calendar/config`, getAuthHeaders()),
        axios.get(`${API}/admin/calendar/prices`, getAuthHeaders()),
        axios.get(`${API}/admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/admin/calendar/blocked`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/overbookings?status=open`, getAuthHeaders()).catch(() => ({ data: { overbookings: [] } })),
        axios.get(`${API}/admin/public-base-url`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      setConfig(configRes.data);
      setPrices(pricesRes.data);
      setBookings(bookingsRes.data);
      setBlocked(blockedRes.data || []);
      
      // Set public base URL config
      if (publicBaseUrlRes.data) {
        setPublicBaseUrlConfig(publicBaseUrlRes.data);
        setPublicBaseUrlInput(publicBaseUrlRes.data.override_url || "");
      }
      
      // Extract overbooking dates for calendar display
      const obDates = [];
      (overbookingRes.data.overbookings || []).forEach(ob => {
        (ob.overlap_dates || []).forEach(date => {
          if (!obDates.includes(date)) obDates.push(date);
        });
      });
      setOverbookingDates(obDates);
      
      // Mark initial load as complete (prevents auto-save on first render)
      if (isInitial) {
        setTimeout(() => {
          isInitialLoad.current = false;
        }, 500);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load calendar data");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  // Load sync logs when tab changes to "sync" and mark as visited
  useEffect(() => {
    if (activeTab === "sync") {
      fetchSyncLogs();
      // Mark sync errors as visited to clear badges
      if (markAsVisited) {
        markAsVisited("sync_errors");
      }
    }
  }, [activeTab]);

  const saveAndSync = async () => {
    setSaving(true);
    setSyncing(true);
    try {
      // 1. Save all config settings
      await axios.put(`${API}/admin/calendar/config`, config, getAuthHeaders());
      console.log("Config saved:", config);
      
      // 2. Update scheduler interval if changed
      if (config.sync_interval_minutes) {
        await axios.post(`${API}/admin/calendar/sync-interval?interval=${config.sync_interval_minutes}`, {}, getAuthHeaders());
        console.log("Scheduler interval updated:", config.sync_interval_minutes);
      }
      
      // 3. Sync iCal if URL is set
      let syncMessage = "Settings saved.";
      if (config.ical_url) {
        const response = await axios.post(`${API}/admin/calendar/sync`, {}, getAuthHeaders());
        syncMessage = `Settings saved & calendar synced. ${response.data.blocked_count || 0} blocked dates found.`;
      }
      
      toast.success(syncMessage);
      
      // 4. Refresh data and sync status to verify
      await fetchData();
      await fetchSyncStatus();
      
      // 5. Show verification toast
      toast.info(`Verified: max_guests=${config.max_guests}, min_nights=${config.min_nights}, cleaning_fee=${config.cleaning_fee}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Save/Sync failed");
    } finally {
      setSaving(false);
      setSyncing(false);
    }
  };

  const addPrice = async () => {
    if (!newPrice.start_date || !newPrice.end_date || !newPrice.price_per_night) {
      toast.error("Please fill all required fields");
      return;
    }
    
    // Validazione: start_date deve essere <= end_date
    if (newPrice.start_date > newPrice.end_date) {
      toast.error("La data di inizio deve essere precedente o uguale alla data di fine");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/calendar/prices`, {
        start_date: newPrice.start_date,
        end_date: newPrice.end_date,
        price_per_night: parseFloat(newPrice.price_per_night),
        min_nights: newPrice.min_nights ? parseInt(newPrice.min_nights) : null,
        notes: newPrice.notes || null
      }, getAuthHeaders());
      toast.success("Price rule added");
      setShowAddPrice(false);
      setNewPrice({ start_date: "", end_date: "", price_per_night: "", min_nights: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add price");
    }
  };

  const deletePrice = async (priceId) => {
    setDeletingPriceId(priceId);
    setDeletingPriceBulk(false);
    setDeletePriceDialogOpen(true);
  };
  
  const confirmDeletePrice = async () => {
    try {
      if (deletingPriceBulk) {
        await Promise.all(selectedPrices.map(id => 
          axios.delete(`${API}/admin/calendar/prices/${id}`, getAuthHeaders())
        ));
        toast.success(`${selectedPrices.length} regole eliminate definitivamente`);
        setSelectedPrices([]);
      } else {
        await axios.delete(`${API}/admin/calendar/prices/${deletingPriceId}`, getAuthHeaders());
        toast.success("Regola prezzo eliminata definitivamente");
      }
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeletePriceDialogOpen(false);
      setDeletingPriceId(null);
      setDeletingPriceBulk(false);
    }
  };

  // Toggle single price selection
  const togglePriceSelection = (priceId) => {
    setSelectedPrices(prev => 
      prev.includes(priceId) 
        ? prev.filter(id => id !== priceId)
        : [...prev, priceId]
    );
  };

  // Toggle all prices selection
  const toggleAllPrices = () => {
    setSelectedPrices(prev => 
      prev.length === prices.length ? [] : prices.map(p => p.price_id)
    );
  };

  // Delete selected prices
  const deleteSelectedPrices = async () => {
    setDeletingPriceId(null);
    setDeletingPriceBulk(true);
    setDeletePriceDialogOpen(true);
  };

  const updateBookingStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${requestId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const [deleteBookingDialogOpen, setDeleteBookingDialogOpen] = useState(false);
  const [deletingBookingId, setDeletingBookingId] = useState(null);
  
  const deleteBooking = async (requestId) => {
    setDeletingBookingId(requestId);
    setDeleteBookingDialogOpen(true);
  };
  
  const confirmDeleteBooking = async () => {
    try {
      await axios.delete(`${API}/admin/bookings/${deletingBookingId}`, getAuthHeaders());
      toast.success("Prenotazione eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteBookingDialogOpen(false);
      setDeletingBookingId(null);
    }
  };

  const exportBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // Public Base URL functions
  const savePublicBaseUrl = async () => {
    if (!publicBaseUrlPassword) {
      toast.error("Inserisci la password admin");
      return;
    }
    
    setSavingPublicBaseUrl(true);
    try {
      const response = await axios.put(`${API}/admin/public-base-url`, {
        base_url: publicBaseUrlInput.trim() || null,
        admin_password: publicBaseUrlPassword
      }, getAuthHeaders());
      
      toast.success(response.data.message || "Base URL aggiornato");
      setShowPasswordModal(false);
      setPublicBaseUrlPassword("");
      
      // Refresh config
      const configRes = await axios.get(`${API}/admin/public-base-url`, getAuthHeaders());
      setPublicBaseUrlConfig(configRes.data);
      setPublicBaseUrlInput(configRes.data.override_url || "");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSavingPublicBaseUrl(false);
    }
  };

  const resetPublicBaseUrl = async () => {
    setConfirmModalConfig({
      title: "Ripristina Modalità Automatica",
      message: "Vuoi ripristinare la modalità automatica per l'URL pubblico?",
      variant: "question",
      onConfirm: async () => {
        setConfirmModalOpen(false);
        setSavingPublicBaseUrl(true);
        try {
          const response = await axios.post(`${API}/admin/public-base-url/reset`, {
            confirm: true
          }, getAuthHeaders());
          
          toast.success(response.data.message || "URL tornato in modalità automatica");
          
          // Refresh config
          const configRes = await axios.get(`${API}/admin/public-base-url`, getAuthHeaders());
          setPublicBaseUrlConfig(configRes.data);
          setPublicBaseUrlInput("");
        } catch (error) {
          toast.error(error.response?.data?.detail || "Errore durante il ripristino");
        } finally {
          setSavingPublicBaseUrl(false);
        }
      }
    });
    setConfirmModalOpen(true);
  };

  const testIcalExportUrl = async () => {
    setTestingIcalUrl(true);
    setIcalTestResult(null);
    try {
      const response = await axios.get(`${API}/admin/public-base-url/test`, getAuthHeaders());
      setIcalTestResult(response.data);
    } catch (error) {
      setIcalTestResult({
        success: false,
        message: "Errore durante il test"
      });
    } finally {
      setTestingIcalUrl(false);
    }
  };

  const copyIcalExportUrl = () => {
    if (publicBaseUrlConfig?.ical_export_url) {
      navigator.clipboard.writeText(publicBaseUrlConfig.ical_export_url);
      toast.success("URL iCal copiato");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Booking</h1>
      </div>

      <Tabs value={activeTab} onValueChange={(tab) => {
        setActiveTab(tab);
      }}>
        <TabsList className="admin-tabs-list">
          <TabsTrigger value="booking" className="admin-tab-trigger">
            <DollarSign size={16} className="mr-2" /> Booking
          </TabsTrigger>
          <TabsTrigger value="source" className="admin-tab-trigger" data-testid="tab-source">
            <Database size={16} className="mr-2" /> Data Source
          </TabsTrigger>
          <TabsTrigger value="sync" className="admin-tab-trigger relative">
            <History size={16} className="mr-2" /> Sync
            {/* Badge errori di sync */}
            {syncErrorsCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-xs font-bold text-white bg-[#D4AF37] rounded-full">
                {syncErrorsCount > 99 ? "99+" : syncErrorsCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="admin-tab-trigger">
            <Settings size={16} className="mr-2" /> Settings
          </TabsTrigger>
          <a 
            href="/admin/booking/cleaning" 
            target="_blank" 
            rel="noopener noreferrer"
            className="admin-tab-trigger inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-[#78716C] hover:text-[#D4AF37] hover:bg-[#FFFDF7]"
          >
            | Cleaning |
          </a>
        </TabsList>

        {/* Info sotto i tabs - cambia in base al tab attivo */}
        <PageInfoUnderTabs pageKey={`booking_${activeTab}`} />

        {/* Booking Tab - EDITOR for prices, blocks, bookings */}
        <TabsContent value="booking" className="mt-6 space-y-6">
          {/* Airbnb Rate Calculator - Always visible */}
          {config && (
            <AirbnbRateCalculator 
              airbnbRateReduction={config.airbnb_rate_reduction || 5}
              onReductionChange={(value) => setConfig({...config, airbnb_rate_reduction: value})}
            />
          )}

          <div className="admin-card">
            {/* Header compatto */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-4 border-b border-[#E7E5E4]">
              <div>
                <h3 className="font-heading admin-section-title">Gestione Booking</h3>
                <p className="text-sm text-[#78716C]">Imposta tariffe, blocca date, gestisci prenotazioni</p>
              </div>
              <div className="flex flex-wrap gap-3 items-center justify-end">
                <button 
                  onClick={() => document.getElementById('price-rules-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-[#D4AF37] font-bold hover:underline text-sm"
                >
                  Vedi regole
                </button>
              <Dialog open={showAddPrice} onOpenChange={setShowAddPrice}>
                <DialogTrigger asChild>
                  <Button className="bg-[#D4AF37] hover:bg-[#B8962E]" size="sm">
                    <Plus size={14} className="mr-1" /> Regola Prezzo
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Aggiungi Regola Prezzo (Manuale)</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-[#78716C] mb-1 block">Data Inizio *</label>
                        <Input type="date" value={newPrice.start_date} onChange={(e) => setNewPrice({...newPrice, start_date: e.target.value})} />
                      </div>
                      <div>
                        <label className="text-sm text-[#78716C] mb-1 block">Data Fine *</label>
                        <Input type="date" value={newPrice.end_date} onChange={(e) => setNewPrice({...newPrice, end_date: e.target.value})} />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Prezzo per Notte (€) *</label>
                      <Input type="number" step="0.01" placeholder="150.00" value={newPrice.price_per_night} onChange={(e) => setNewPrice({...newPrice, price_per_night: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Notti Minime (opzionale)</label>
                      <Input type="number" placeholder="es. 3" value={newPrice.min_nights} onChange={(e) => setNewPrice({...newPrice, min_nights: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Note (opzionale)</label>
                      <Input placeholder="es. Alta stagione" value={newPrice.notes} onChange={(e) => setNewPrice({...newPrice, notes: e.target.value})} />
                    </div>
                    <Button onClick={addPrice} className="w-full bg-[#1C1917]">Aggiungi Regola</Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </div>
            
            {/* Visual Price Calendar */}
            <PriceCalendarGrid
              prices={(() => {
                // Convert prices array to date->price map with specificity priority
                // More specific ranges (fewer days) take precedence
                const priceMap = {};
                const specificityMap = {};  // Track specificity for each date
                
                prices.forEach(p => {
                  const start = new Date(p.start_date);
                  const end = new Date(p.end_date);
                  const specificity = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                  
                  let current = new Date(p.start_date);
                  while (current <= end) {
                    const dateStr = current.toISOString().split('T')[0];
                    const currentSpecificity = specificityMap[dateStr] || Infinity;
                    
                    // More specific (smaller range) wins
                    if (specificity < currentSpecificity) {
                      priceMap[dateStr] = p.price_per_night;
                      specificityMap[dateStr] = specificity;
                    }
                    current.setDate(current.getDate() + 1);
                  }
                });
                return priceMap;
              })()}
              blockedDates={blocked}
              bookings={bookings}
              bookingWindowMonths={config?.booking_window_months || 12}
              currentYear={sharedYear}
              onYearChange={setSharedYear}
              overbookingDates={overbookingDates}
              onResolveOverbooking={handleResolveOverbooking}
              onPriceChange={async (dates, price) => {
                if (!dates.length || price === undefined) return;
                try {
                  // Raggruppa date consecutive per creare regole efficienti
                  const sortedDates = [...dates].sort();
                  const groups = [];
                  let currentGroup = [sortedDates[0]];
                  
                  for (let i = 1; i < sortedDates.length; i++) {
                    const prevDate = new Date(sortedDates[i - 1]);
                    const currDate = new Date(sortedDates[i]);
                    const diffDays = (currDate - prevDate) / (1000 * 60 * 60 * 24);
                    
                    if (diffDays === 1) {
                      // Consecutivo, aggiungi al gruppo corrente
                      currentGroup.push(sortedDates[i]);
                    } else {
                      // Non consecutivo, salva gruppo e inizia nuovo
                      groups.push(currentGroup);
                      currentGroup = [sortedDates[i]];
                    }
                  }
                  groups.push(currentGroup); // Aggiungi ultimo gruppo
                  
                  // Crea una regola per ogni gruppo di date consecutive
                  for (const group of groups) {
                    const newRule = {
                      price_id: `price_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
                      start_date: group[0],
                      end_date: group[group.length - 1],
                      price_per_night: price,
                      notes: `Set via calendar on ${formatDisplayDate(new Date())}`
                    };
                    await axios.post(`${API}/admin/calendar/prices`, newRule, getAuthHeaders());
                  }
                  toast.success(`Price €${price} set for ${dates.length} day${dates.length > 1 ? 's' : ''}`);
                  fetchData();
                } catch (error) {
                  toast.error("Failed to save price");
                }
              }}
              onBlockDates={async (dates) => {
                if (!dates.length) return;
                try {
                  await axios.post(`${API}/admin/calendar/block-bulk`, { dates }, getAuthHeaders());
                  toast.success(`${dates.length} date${dates.length > 1 ? 's' : ''} blocked`);
                  fetchData();
                } catch (error) {
                  toast.error("Failed to block dates");
                }
              }}
              onUnblockDates={async (dates) => {
                if (!dates.length) return;
                try {
                  await axios.post(`${API}/admin/calendar/unblock-bulk`, { dates }, getAuthHeaders());
                  toast.success(`${dates.length} date${dates.length > 1 ? 's' : ''} unblocked`);
                  fetchData();
                } catch (error) {
                  toast.error("Failed to unblock dates");
                }
              }}
              onConfirmDates={async (dates, guestName) => {
                if (!dates.length) return;
                try {
                  const response = await axios.post(`${API}/admin/calendar/confirm-manual`, { 
                    dates, 
                    guest_name: guestName || "Conferma Manuale"
                  }, getAuthHeaders());
                  toast.success(`Conferma creata - #${response.data.booking_number}`);
                  fetchData();
                } catch (error) {
                  const errorMsg = error.response?.data?.detail || "Errore nella conferma";
                  toast.error(errorMsg);
                }
              }}
              onRemoveConfirmation={async (dates) => {
                if (!dates.length) return;
                try {
                  await axios.post(`${API}/admin/calendar/remove-confirmation`, { dates }, getAuthHeaders());
                  toast.success("Confirmation removed");
                  fetchData();
                } catch (error) {
                  toast.error("Failed to remove confirmation");
                }
              }}
              onCancelBooking={(bookingIds) => {
                if (!bookingIds.length) return;
                setBookingsToDelete(bookingIds);
                setDeleteBookingModalOpen(true);
              }}
              onNotesUpdate={() => fetchData()}
              getAuthHeaders={getAuthHeaders}
            />
            
            {/* Price Rules List - More Prominent */}
            <div id="price-rules-section" className="mt-6 border-2 border-[#D4AF37] rounded-lg p-4 bg-[#FFFDF7]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-heading text-lg text-[#1C1917] flex items-center gap-2">
                    📋 Regole Prezzo
                    <span className="text-sm font-normal bg-[#D4AF37] text-white px-2 py-0.5 rounded-full">
                      {prices.length}
                    </span>
                  </h3>
                  {prices.length > 0 && (
                    <label className="flex items-center gap-2 cursor-pointer ml-4">
                      <input 
                        type="checkbox" 
                        checked={selectedPrices.length === prices.length && prices.length > 0} 
                        onChange={toggleAllPrices} 
                        className="w-4 h-4 accent-[#D4AF37]" 
                      />
                      <span className="text-sm text-[#78716C]">Seleziona tutte</span>
                    </label>
                  )}
                </div>
                {selectedPrices.length > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={deleteSelectedPrices}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 size={14} className="mr-1" /> Elimina {selectedPrices.length}
                  </Button>
                )}
              </div>
              
              {prices.length === 0 ? (
                <p className="text-sm text-[#78716C] italic">Nessuna regola prezzo definita. Usa il calendario sopra per aggiungere prezzi.</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {prices.map(price => (
                    <div 
                      key={price.price_id} 
                      className={`border p-3 rounded-lg flex items-center justify-between text-sm transition-colors ${
                        selectedPrices.includes(price.price_id) 
                          ? 'bg-[#FDF9EF] border-[#D4AF37]' 
                          : 'bg-white border-[#E7E5E4]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input 
                          type="checkbox" 
                          checked={selectedPrices.includes(price.price_id)} 
                          onChange={() => togglePriceSelection(price.price_id)} 
                          className="w-4 h-4 accent-[#D4AF37]" 
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{price.start_date} → {price.end_date}</span>
                          <span className="text-[#D4AF37] font-semibold">€{price.price_per_night}/notte</span>
                          {price.notes && <span className="text-[#78716C] text-xs">({price.notes})</span>}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deletePrice(price.price_id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6 space-y-6">
          {config && (
            <>
              {/* STRIPE CREDENTIALS SECTION */}
              <div className="admin-card border-2 border-[#D4AF37]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading admin-section-title">Stripe Credentials</h3>
                  {stripeCredentials && (
                    <div className="flex items-center gap-2">
                      <span className={`text-sm px-2 py-1 rounded ${stripeCredentials.active_mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {stripeCredentials.active_mode === 'live' ? '🟢 LIVE' : '🟡 TEST'}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Webhook URL (read-only) */}
                {stripeCredentials?.webhook_url && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <label className="text-xs text-[#78716C] uppercase tracking-wide mb-1 block">Webhook URL (configura su Stripe)</label>
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-white px-2 py-1 rounded border flex-1 overflow-x-auto">{stripeCredentials.webhook_url}</code>
                      <Button size="sm" variant="outline" onClick={() => {navigator.clipboard.writeText(stripeCredentials.webhook_url); toast.success("URL copiato!")}}>
                        Copia
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Mode Toggle */}
                <div className="mb-6 p-4 bg-[#FAFAF9] rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Modalità Pagamenti</p>
                      <p className="text-sm text-[#78716C]">
                        {stripeCredentials?.active_mode === 'live' 
                          ? 'I pagamenti sono REALI' 
                          : 'Modalità test - nessun addebito reale'}
                      </p>
                    </div>
                    <Button
                      className={stripeCredentials?.active_mode === 'live' 
                        ? 'bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6' 
                        : 'bg-green-600 hover:bg-green-700 text-white font-semibold px-6'}
                      onClick={() => {
                        const newMode = stripeCredentials?.active_mode === 'live' ? 'test' : 'live';
                        if (newMode === 'live' && !stripeCredentials?.has_live_credentials) {
                          toast.error("Configura prima le credenziali LIVE");
                          return;
                        }
                        const isGoingLive = newMode === 'live';
                        setConfirmModalConfig({
                          title: isGoingLive ? "Attivare Modalità LIVE" : "Tornare a Modalità TEST",
                          message: isGoingLive 
                            ? "Stai per attivare la modalità LIVE. I pagamenti saranno REALI e verranno addebitati sulle carte dei clienti."
                            : "Stai per tornare alla modalità TEST. I pagamenti non saranno più reali.",
                          variant: isGoingLive ? "warning" : "question",
                          onConfirm: async () => {
                            setConfirmModalOpen(false);
                            try {
                              await axios.post(`${API}/admin/stripe/toggle-mode`, {}, getAuthHeaders());
                              fetchStripeCredentials();
                              toast.success(`Modalità ${newMode.toUpperCase()} attivata`);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || "Errore");
                            }
                          }
                        });
                        setConfirmModalOpen(true);
                      }}
                      disabled={!stripeCredentials}
                    >
                      {stripeCredentials?.active_mode === 'live' ? '⚡ Passa a TEST' : '🚀 Passa a LIVE'}
                    </Button>
                  </div>
                </div>
                
                {/* TEST Credentials */}
                <div className="mb-6">
                  <h4 className="font-medium text-[#78716C] mb-3 flex items-center gap-2">
                    <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded">TEST</span>
                    Credenziali Test
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Publishable Key (pk_test_...)</label>
                      <Input 
                        value={stripeTestPublishable} 
                        onChange={(e) => setStripeTestPublishable(e.target.value)}
                        placeholder={stripeCredentials?.publishable_key_test || "pk_test_..."}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Secret Key (sk_test_...)</label>
                      <Input 
                        type="password"
                        value={stripeTestSecret} 
                        onChange={(e) => setStripeTestSecret(e.target.value)}
                        placeholder={stripeCredentials?.secret_key_test_masked || "sk_test_..."}
                      />
                      {stripeCredentials?.secret_key_test_masked && (
                        <p className="text-xs text-[#78716C] mt-1">Attuale: {stripeCredentials.secret_key_test_masked}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-[#78716C] mb-1 block">Webhook Signing Secret (whsec_...)</label>
                      <Input 
                        type="password"
                        value={stripeTestWebhook} 
                        onChange={(e) => setStripeTestWebhook(e.target.value)}
                        placeholder={stripeCredentials?.webhook_secret_test_masked || "whsec_..."}
                      />
                      {stripeCredentials?.webhook_secret_test_masked && (
                        <p className="text-xs text-[#78716C] mt-1">Attuale: {stripeCredentials.webhook_secret_test_masked}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* LIVE Credentials */}
                <div className="mb-6">
                  <h4 className="font-medium text-[#78716C] mb-3 flex items-center gap-2">
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">LIVE</span>
                    Credenziali Live
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Publishable Key (pk_live_...)</label>
                      <Input 
                        value={stripeLivePublishable} 
                        onChange={(e) => setStripeLivePublishable(e.target.value)}
                        placeholder={stripeCredentials?.publishable_key_live || "pk_live_..."}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Secret Key (sk_live_...)</label>
                      <Input 
                        type="password"
                        value={stripeLiveSecret} 
                        onChange={(e) => setStripeLiveSecret(e.target.value)}
                        placeholder={stripeCredentials?.secret_key_live_masked || "sk_live_..."}
                      />
                      {stripeCredentials?.secret_key_live_masked && (
                        <p className="text-xs text-[#78716C] mt-1">Attuale: {stripeCredentials.secret_key_live_masked}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-[#78716C] mb-1 block">Webhook Signing Secret (whsec_...)</label>
                      <Input 
                        type="password"
                        value={stripeLiveWebhook} 
                        onChange={(e) => setStripeLiveWebhook(e.target.value)}
                        placeholder={stripeCredentials?.webhook_secret_live_masked || "whsec_..."}
                      />
                      {stripeCredentials?.webhook_secret_live_masked && (
                        <p className="text-xs text-[#78716C] mt-1">Attuale: {stripeCredentials.webhook_secret_live_masked}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Save Button */}
                <div className="flex justify-end">
                  <Button 
                    onClick={saveStripeCredentials}
                    disabled={!stripeTestPublishable && !stripeTestSecret && !stripeTestWebhook && !stripeLivePublishable && !stripeLiveSecret && !stripeLiveWebhook}
                  >
                    <Save size={16} className="mr-2" />
                    Salva Credenziali
                  </Button>
                </div>
                
                {/* Warning */}
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    ⚠️ <strong>Importante:</strong> Le Secret Key e i Webhook Secret non sono visualizzabili dopo il salvataggio. Conserva una copia sicura delle tue chiavi.
                  </p>
                </div>
              </div>

              {/* NUOVA SEZIONE: Finestra di Prenotazione */}
              <div className="admin-card border-2 border-[#D4AF37]">
                <h3 className="font-heading admin-section-title mb-2">Finestra di Prenotazione</h3>
                <p className="text-sm text-[#78716C] mb-4">
                  Limita quanto in anticipo gli utenti possono prenotare dal form pubblico.
                </p>
                <div>
                  <label className="text-sm text-[#78716C] mb-2 block">Prenotabile fino a (mesi)</label>
                  <Select 
                    value={String(config.booking_window_months || 12)} 
                    onValueChange={(v) => setConfig({...config, booking_window_months: parseInt(v)})}
                  >
                    <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 mesi</SelectItem>
                      <SelectItem value="6">6 mesi</SelectItem>
                      <SelectItem value="9">9 mesi</SelectItem>
                      <SelectItem value="12">12 mesi (1 anno)</SelectItem>
                      <SelectItem value="18">18 mesi</SelectItem>
                      <SelectItem value="24">24 mesi (2 anni)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#78716C] mt-1">
                    Gli utenti potranno prenotare solo fino a oggi + {config.booking_window_months || 12} mesi.
                  </p>
                </div>
              </div>

              <div className="admin-card">
                <h3 className="font-heading admin-section-title mb-6">Property Rules</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Maximum Guests</label>
                    <Input type="number" value={config.max_guests} onChange={(e) => setConfig({...config, max_guests: parseInt(e.target.value) || 6})} />
                  </div>
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Minimum Nights</label>
                    <Input type="number" value={config.min_nights} onChange={(e) => setConfig({...config, min_nights: parseInt(e.target.value) || 2})} />
                  </div>
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Base Guests (included in price)</label>
                    <Input type="number" value={config.base_guests} onChange={(e) => setConfig({...config, base_guests: parseInt(e.target.value) || 2})} />
                  </div>
                </div>
              </div>

              <div className="admin-card">
                <h3 className="font-heading admin-section-title mb-6">Pricing</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Extra Guest Fee (€/guest/night)</label>
                    <Input type="number" step="0.01" value={config.extra_guest_fee} onChange={(e) => setConfig({...config, extra_guest_fee: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Infant Fee (0 years, €/infant/night)</label>
                    <Input type="number" step="0.01" value={config.infant_fee} onChange={(e) => setConfig({...config, infant_fee: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Cleaning Fee (€)</label>
                    <Input type="number" step="0.01" value={config.cleaning_fee} onChange={(e) => setConfig({...config, cleaning_fee: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Currency</label>
                    <Select value={config.currency} onValueChange={(v) => setConfig({...config, currency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="admin-card">
                <h3 className="font-heading admin-section-title mb-6">Tourist Tax</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Tourist Tax (€/person/night)</label>
                    <Input type="number" step="0.01" value={config.tourist_tax_per_person} onChange={(e) => setConfig({...config, tourist_tax_per_person: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Max Nights for Tax</label>
                    <Input type="number" value={config.tourist_tax_max_nights} onChange={(e) => setConfig({...config, tourist_tax_max_nights: parseInt(e.target.value) || 10})} />
                    <p className="text-xs text-[#78716C] mt-1">Tax applies only for the first N nights</p>
                  </div>
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Tax Exemption Age (years)</label>
                    <Input type="number" value={config.tourist_tax_exemption_age || 10} onChange={(e) => setConfig({...config, tourist_tax_exemption_age: parseInt(e.target.value) || 10})} />
                    <p className="text-xs text-[#78716C] mt-1">Children ≤ this age are exempt</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Data Source Tab */}
        <TabsContent value="source" className="mt-6 space-y-6">
          {config && (
            <>
              {/* Header with Add Platform button */}
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl">Calendar Data Sources</h3>
                <Button
                  onClick={() => setShowAddPlatform(true)}
                  className="bg-[#D4AF37] hover:bg-[#B8972E] text-white"
                >
                  <Plus size={16} className="mr-2" />
                  Aggiungi Piattaforma
                </Button>
              </div>

              {/* Platform Cards */}
              {platforms.length === 0 ? (
                <div className="admin-card text-center py-12">
                  <Database size={48} className="mx-auto mb-4 text-[#78716C] opacity-50" />
                  <p className="text-[#78716C] mb-4">Nessuna piattaforma configurata</p>
                  <Button
                    onClick={() => setShowAddPlatform(true)}
                    variant="outline"
                  >
                    <Plus size={16} className="mr-2" />
                    Aggiungi la prima piattaforma
                  </Button>
                </div>
              ) : (
                platforms.map((platform) => (
                  <div key={platform.platform_id} className="admin-card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading admin-section-title flex items-center gap-2">
                        <Database size={20} className="text-[#D4AF37]" />
                        Calendar Data Source – {platform.platform_name}
                      </h3>
                      <div className="flex items-center gap-3">
                        {/* Toggle Abilitato */}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className={`text-sm font-medium ${platform.enabled ? 'text-green-600' : 'text-[#78716C]'}`}>
                            {platform.enabled ? 'Abilitato' : 'Disabilitato'}
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={platform.enabled}
                            onClick={() => {
                              const updated = { ...platform, enabled: !platform.enabled };
                              setPlatforms(platforms.map(p => p.platform_id === platform.platform_id ? updated : p));
                              updatePlatform(updated);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${platform.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${platform.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => syncPlatform(platform)}
                          disabled={syncingPlatform === platform.platform_id || !platform.enabled}
                        >
                          <RefreshCw size={14} className={`mr-1 ${syncingPlatform === platform.platform_id ? 'animate-spin' : ''}`} />
                          Sync
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deletePlatform(platform)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Platform Name */}
                      <div>
                        <label className="text-sm text-[#78716C] mb-2 block">Nome Piattaforma</label>
                        <Input 
                          value={platform.platform_name}
                          onChange={(e) => {
                            const updated = { ...platform, platform_name: e.target.value };
                            setPlatforms(platforms.map(p => p.platform_id === platform.platform_id ? updated : p));
                          }}
                          onBlur={() => updatePlatform(platform)}
                          placeholder="es. Airbnb, Booking.com, Vrbo"
                        />
                      </div>

                      {/* iCal Import URL (Platform → Suite Lucina) */}
                      <div className="bg-white border border-[#E7E5E4] rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Download size={16} className="text-[#D4AF37]" />
                          <label className="text-sm font-medium text-[#1C1917]">iCal Import ({platform.platform_name} → Suite Lucina)</label>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-[#78716C] mb-1 block">URL del calendario iCal</label>
                            <Input 
                              type="url" 
                              placeholder="https://..." 
                              value={platform.ical_url || ""} 
                              onChange={(e) => {
                                const updated = { ...platform, ical_url: e.target.value };
                                setPlatforms(platforms.map(p => p.platform_id === platform.platform_id ? updated : p));
                              }}
                              onBlur={() => updatePlatform(platform)}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block">Intervallo Sync (minuti)</label>
                              <Input 
                                type="number" 
                                min="5" 
                                max="1440" 
                                value={platform.sync_interval_minutes || 60} 
                                onChange={(e) => {
                                  const updated = { ...platform, sync_interval_minutes: parseInt(e.target.value) || 60 };
                                  setPlatforms(platforms.map(p => p.platform_id === platform.platform_id ? updated : p));
                                }}
                                onBlur={() => updatePlatform(platform)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sync Status */}
                      <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                        <label className="text-sm font-medium text-[#1C1917] mb-3 block">Stato Sincronizzazione</label>
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-xs text-[#78716C] mb-1 block">Ultima Sincronizzazione</label>
                            <p className="font-semibold text-[#1C1917]">
                              {platform.last_sync ? (
                                formatDisplayDateTime(platform.last_sync)
                              ) : (
                                <span className="text-[#78716C] italic">Mai sincronizzato</span>
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            {platform.sync_status === 'success' && (
                              <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                <CheckCircle size={16} /> Completato
                              </span>
                            )}
                            {platform.sync_status === 'error' && (
                              <span className="inline-flex items-center gap-1 text-red-600 text-sm">
                                <AlertCircle size={16} /> Errore
                              </span>
                            )}
                            {platform.sync_status === 'syncing' && (
                              <span className="inline-flex items-center gap-1 text-blue-600 text-sm">
                                <RefreshCw size={16} className="animate-spin" /> Sincronizzazione...
                              </span>
                            )}
                          </div>
                        </div>
                        {platform.sync_error && (
                          <p className="text-sm text-red-600 mt-2">{platform.sync_error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}

              {/* Add Platform Dialog */}
              <Dialog open={showAddPlatform} onOpenChange={setShowAddPlatform}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Aggiungi Nuova Piattaforma</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm text-[#78716C] mb-2 block">Nome Piattaforma *</label>
                      <Input 
                        placeholder="es. Booking.com, Vrbo, HomeAway"
                        value={newPlatform.platform_name}
                        onChange={(e) => setNewPlatform({...newPlatform, platform_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#78716C] mb-2 block">iCal URL *</label>
                      <Input 
                        type="url"
                        placeholder="https://..."
                        value={newPlatform.ical_url}
                        onChange={(e) => setNewPlatform({...newPlatform, ical_url: e.target.value})}
                      />
                      <p className="text-xs text-[#78716C] mt-1">
                        Inserisci l'URL del feed iCal della piattaforma
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-[#78716C] mb-2 block">Intervallo Sync (minuti)</label>
                      <Input 
                        type="number"
                        min="5"
                        max="1440"
                        value={newPlatform.sync_interval_minutes}
                        onChange={(e) => setNewPlatform({...newPlatform, sync_interval_minutes: parseInt(e.target.value) || 60})}
                      />
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                      <Button variant="outline" onClick={() => setShowAddPlatform(false)}>
                        Annulla
                      </Button>
                      <Button onClick={addPlatform} className="bg-[#D4AF37] hover:bg-[#B8972E] text-white">
                        <Plus size={16} className="mr-2" />
                        Aggiungi
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Password Modal for Public Base URL */}
              <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Shield size={20} className="text-[#D4AF37]" />
                      Conferma Sicurezza
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-[#78716C]">
                      Per modificare il Base URL pubblico, inserisci la tua password admin.
                    </p>
                    <div>
                      <label className="text-sm text-[#78716C] mb-2 block">Password Admin</label>
                      <Input 
                        type="password"
                        placeholder="Inserisci password"
                        value={publicBaseUrlPassword}
                        onChange={(e) => setPublicBaseUrlPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && savePublicBaseUrl()}
                      />
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                      <Button variant="outline" onClick={() => {
                        setShowPasswordModal(false);
                        setPublicBaseUrlPassword("");
                      }}>
                        Annulla
                      </Button>
                      <Button 
                        onClick={savePublicBaseUrl} 
                        disabled={savingPublicBaseUrl || !publicBaseUrlPassword}
                        className="bg-[#D4AF37] hover:bg-[#B8972E] text-white"
                      >
                        {savingPublicBaseUrl ? (
                          <RefreshCw size={16} className="mr-2 animate-spin" />
                        ) : (
                          <Save size={16} className="mr-2" />
                        )}
                        Conferma e Salva
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Public Base URL Section (iCal Export) */}
              <div className="admin-card">
                <div className="flex items-center gap-2 mb-4">
                  <ExternalLink size={20} className="text-[#D4AF37]" />
                  <h3 className="font-heading admin-section-title">Base URL Pubblico (iCal Export)</h3>
                </div>
                
                {/* Info text */}
                <p className="text-sm text-[#78716C] mb-4">
                  Nota sul funzionamento dell'URL iCal: L'URL mostrato qui è quello da fornire alle piattaforme di prenotazione (es. Airbnb).
                  Di default, il sistema lo calcola automaticamente in base all'indirizzo pubblico corrente del sito.
                  È possibile impostare manualmente un Base URL pubblico in caso di deploy su domini o infrastrutture diverse.
                  In qualsiasi momento è possibile tornare al comportamento automatico tramite "Ripristina automatico".
                  L'URL risultante è sempre quello corretto da utilizzare sulle piattaforme.
                </p>

                {/* Status indicator */}
                <div className="flex items-center gap-2 mb-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
                    publicBaseUrlConfig?.is_override_active 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      publicBaseUrlConfig?.is_override_active ? 'bg-yellow-500' : 'bg-green-500'
                    }`}></span>
                    {publicBaseUrlConfig?.is_override_active ? 'Override manuale attivo' : 'Automatico attivo'}
                  </span>
                </div>

                {/* Base URL Input */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-[#78716C] mb-2 block">Base URL Pubblico (override)</label>
                    <div className="flex gap-2">
                      <Input 
                        type="url"
                        placeholder="Auto (calcolato automaticamente dal sistema)"
                        value={publicBaseUrlInput}
                        onChange={(e) => setPublicBaseUrlInput(e.target.value)}
                        className="flex-1"
                      />
                      <Button 
                        variant="outline"
                        onClick={() => setShowPasswordModal(true)}
                        disabled={savingPublicBaseUrl}
                      >
                        <Save size={16} className="mr-1" /> Salva
                      </Button>
                      {publicBaseUrlConfig?.is_override_active && (
                        <Button 
                          variant="outline"
                          onClick={resetPublicBaseUrl}
                          disabled={savingPublicBaseUrl}
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        >
                          <RotateCcw size={16} className="mr-1" /> Ripristina automatico
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-[#A8A29E] mt-1">
                      Lascia vuoto per usare l'URL automatico. Se imposti un override, inserisci solo il dominio (es. https://www.tuodominio.it)
                    </p>
                  </div>

                  {/* Effective iCal URL display */}
                  <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                    <label className="text-sm font-medium text-[#1C1917] mb-2 block">URL iCal da fornire alle piattaforme</label>
                    <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-[#E7E5E4]">
                      <code className="text-sm text-[#1C1917] flex-1 break-all font-mono">
                        {publicBaseUrlConfig?.ical_export_url || `${window.location.origin}/api/ical/airbnb.ics`}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyIcalExportUrl}
                        className="text-xs whitespace-nowrap"
                      >
                        <Copy size={12} className="mr-1" /> Copia
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={testIcalExportUrl}
                        disabled={testingIcalUrl}
                        className="text-xs whitespace-nowrap"
                      >
                        {testingIcalUrl ? (
                          <RefreshCw size={12} className="mr-1 animate-spin" />
                        ) : (
                          <ExternalLink size={12} className="mr-1" />
                        )}
                        Testa URL
                      </Button>
                    </div>
                    
                    {/* Test result */}
                    {icalTestResult && (
                      <div className={`mt-3 p-3 rounded-lg text-sm ${
                        icalTestResult.success 
                          ? 'bg-green-50 text-green-800 border border-green-200' 
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          {icalTestResult.success ? (
                            <CheckCircle size={16} />
                          ) : (
                            <AlertCircle size={16} />
                          )}
                          <span>{icalTestResult.message}</span>
                        </div>
                        {icalTestResult.status_code && (
                          <p className="text-xs mt-1 opacity-75">HTTP Status: {icalTestResult.status_code}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Last modified info */}
                  {publicBaseUrlConfig?.last_modified && (
                    <p className="text-xs text-[#A8A29E]">
                      Ultima modifica: {formatDisplayDateTime(publicBaseUrlConfig.last_modified)}
                      {publicBaseUrlConfig.modified_by && ` da ${publicBaseUrlConfig.modified_by}`}
                    </p>
                  )}
                </div>
              </div>

              {/* Channel Manager Section */}
              <div className="admin-card">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading admin-section-title">Channel Manager</h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className={`text-sm font-medium ${config.channel_manager_enabled ? 'text-green-600' : 'text-[#78716C]'}`}>
                      {config.channel_manager_enabled ? 'Abilitato' : 'Disabilitato'}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={config.channel_manager_enabled}
                      onClick={() => setConfig({...config, channel_manager_enabled: !config.channel_manager_enabled})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.channel_manager_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.channel_manager_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </label>
                </div>
                <div className="space-y-4">

                  {config.channel_manager_enabled && (
                    <>
                      <div>
                        <label className="text-sm text-[#78716C] mb-2 block">Channel Manager Type</label>
                        <Select value={config.channel_manager_type || ""} onValueChange={(v) => setConfig({...config, channel_manager_type: v})}>
                          <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beds24">Beds24</SelectItem>
                            <SelectItem value="smoobu">Smoobu</SelectItem>
                            <SelectItem value="lodgify">Lodgify</SelectItem>
                            <SelectItem value="hostaway">Hostaway</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm text-[#78716C] mb-2 block">API URL</label>
                        <Input placeholder="https://api.provider.com" value={config.channel_manager_api_url || ""} onChange={(e) => setConfig({...config, channel_manager_api_url: e.target.value})} />
                      </div>
                      <div>
                        <label className="text-sm text-[#78716C] mb-2 block">API Key</label>
                        <Input type="password" placeholder="Your API key" value={config.channel_manager_api_key || ""} onChange={(e) => setConfig({...config, channel_manager_api_key: e.target.value})} />
                      </div>
                      <div>
                        <label className="text-sm text-[#78716C] mb-2 block">Property ID</label>
                        <Input placeholder="Property/Listing ID" value={config.channel_manager_property_id || ""} onChange={(e) => setConfig({...config, channel_manager_property_id: e.target.value})} />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className={`admin-card ${!config.channel_manager_enabled ? 'opacity-60' : ''}`}>
                <h3 className="font-heading admin-section-title mb-6">Price Mode</h3>
                <div>
                  <label className="text-sm text-[#78716C] mb-2 block">Price Source</label>
                  <Select 
                    value={config.price_mode} 
                    onValueChange={(v) => setConfig({...config, price_mode: v})}
                    disabled={!config.channel_manager_enabled && config.price_mode !== "manual"}
                  >
                    <SelectTrigger className={!config.channel_manager_enabled ? 'bg-gray-100' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual (use price rules above)</SelectItem>
                      <SelectItem 
                        value="channel_manager" 
                        disabled={!config.channel_manager_enabled}
                        className={!config.channel_manager_enabled ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        Channel Manager (fetch from CM) {!config.channel_manager_enabled && '— Richiede CM attivo'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#78716C] mt-1">
                    {!config.channel_manager_enabled 
                      ? "Abilita il Channel Manager per utilizzare questa opzione"
                      : config.price_mode === "manual" 
                        ? "Prices will be taken from your manually defined price rules"
                        : "Prices will be fetched from the connected channel manager"}
                  </p>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Sync Logs Tab */}
        <TabsContent value="sync" className="mt-6 space-y-6">
          {/* Modale Semplice Sì/No per tutte le eliminazioni */}
          {showSimpleDeleteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                <h3 className="font-heading text-xl mb-4 text-[#1C1917]">Conferma eliminazione</h3>
                <p className="text-[#78716C] mb-6">
                  {deleteTargetType === "bookings" && selectedBookingLogs.length > 0
                    ? `Eliminare definitivamente ${selectedBookingLogs.length} prenotazion${selectedBookingLogs.length === 1 ? 'e rilevata' : 'i rilevate'}?`
                    : deleteTargetType === "logs" && selectedSyncLogs.length > 0
                    ? `Eliminare definitivamente ${selectedSyncLogs.length} log?`
                    : deleteTargetType === "all_bookings"
                    ? "Eliminare definitivamente tutte le prenotazioni rilevate?"
                    : deleteTargetType === "all_logs"
                    ? "Eliminare definitivamente tutti i log di errore?"
                    : deleteTargetType === "all"
                    ? "Eliminare definitivamente tutti i log?"
                    : deleteTargetType === "single"
                    ? "Eliminare definitivamente questo log?"
                    : "Confermi l'eliminazione?"}
                </p>
                <div className="flex gap-3 justify-end">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowSimpleDeleteModal(false);
                      setDeleteTargetType("");
                    }}
                  >
                    No
                  </Button>
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={executeSimpleDelete}
                    disabled={deletingAllLogs}
                  >
                    {deletingAllLogs ? (
                      <>
                        <RefreshCw size={16} className="animate-spin mr-2" />
                        Eliminazione...
                      </>
                    ) : (
                      "Sì"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Stato Generale della Sincronizzazione iCal */}
          <div className="admin-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl">Stato Sincronizzazione iCal</h3>
              <Button
                onClick={() => fetchSyncLogs()}
                variant="outline"
                disabled={loadingSyncLogs}
                size="sm"
              >
                <RefreshCw size={14} className={`mr-1 ${loadingSyncLogs ? 'animate-spin' : ''}`} />
                Sync
              </Button>
            </div>

            {loadingSyncLogs && !syncOverview ? (
              <div className="text-center py-8 text-[#78716C]">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                Caricamento...
              </div>
            ) : syncOverview ? (
              <div className="space-y-4">
                {/* Overview Cards - 2 in una riga (rimosso Ultima Sincronizzazione ridondante) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Prenotazioni ultimi 7 giorni */}
                  <div className="p-4 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4]">
                    <p className="text-xs text-[#78716C] uppercase tracking-wide mb-1">Prenotazioni negli ultimi 7 GG</p>
                    <p className="text-2xl font-bold text-[#1C1917]">
                      {importedBookingsStats?.seven_days?.count || 0}
                    </p>
                  </div>

                  {/* Prenotazioni Totali con Delta */}
                  <div className="p-4 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4]">
                    <p className="text-xs text-[#78716C] uppercase tracking-wide mb-1">Sync Prenotazioni Totali</p>
                    <div className="flex items-baseline gap-2">
                      <p className={`text-2xl font-bold ${syncLogs[0]?.delta > 0 ? 'text-[#D4AF37]' : 'text-[#1C1917]'}`}>
                        {importedBookingsStats?.total?.count || 0}
                      </p>
                      {syncLogs[0]?.delta > 0 && (
                        <span className="text-sm font-semibold text-[#D4AF37] bg-[#FEF3C7] px-2 py-0.5 rounded">
                          +{syncLogs[0].delta}
                        </span>
                      )}
                      {syncLogs[0]?.delta < 0 && (
                        <span className="text-sm font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded">
                          {syncLogs[0].delta}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Piattaforme Configurate - Layout a riga singola */}
                {syncOverview.platforms && syncOverview.platforms.length > 0 ? (
                  <div className="mt-4">
                    <p className="text-xs text-[#78716C] uppercase tracking-wide mb-3 text-center">Piattaforme Configurate</p>
                    <div className="space-y-2">
                      {syncOverview.platforms.map((platform, idx) => {
                        // Calcola ore dall'ultima sync per warning visivo
                        let syncAgeHours = 0;
                        let syncWarningLevel = "ok"; // "ok", "warning" (>3h), "critical" (>5h)
                        
                        if (platform.last_sync) {
                          const lastSyncDate = new Date(platform.last_sync);
                          const now = new Date();
                          syncAgeHours = (now - lastSyncDate) / (1000 * 60 * 60);
                          
                          if (syncAgeHours > 5) {
                            syncWarningLevel = "critical";
                          } else if (syncAgeHours > 3) {
                            syncWarningLevel = "warning";
                          }
                        }
                        
                        // Determina colori e badge basati su status E warning temporale
                        let rowClass = "bg-[#FAFAF9] border-[#E7E5E4]";
                        let statusBadge = { text: platform.status, class: "text-yellow-700" };
                        
                        if (platform.status === "error") {
                          rowClass = "bg-red-50 border-red-200";
                          statusBadge = { text: "✗ Errore", class: "text-red-600 font-semibold" };
                        } else if (platform.status === "never_synced") {
                          rowClass = "bg-gray-50 border-gray-200";
                          statusBadge = { text: "Mai sync", class: "text-gray-500" };
                        } else if (platform.status === "success" || platform.status === "success_with_changes") {
                          if (syncWarningLevel === "critical") {
                            rowClass = "bg-red-50 border-red-300";
                            statusBadge = { text: "⚠ Sync > 5h", class: "text-red-600 font-semibold" };
                          } else if (syncWarningLevel === "warning") {
                            rowClass = "bg-orange-50 border-orange-300";
                            statusBadge = { text: "⚠ Sync > 3h", class: "text-orange-600 font-semibold" };
                          } else {
                            rowClass = "bg-green-50 border-green-200";
                            statusBadge = { text: "✓ OK", class: "text-green-700 font-semibold" };
                          }
                        }
                        
                        // Costruisci info per la riga
                        const bookingsCount = platform.total_bookings || 0;
                        const coverageDate = platform.coverage_end ? formatDisplayDate(platform.coverage_end) : null;
                        const lastSyncTime = platform.last_sync ? formatRelativeTime(platform.last_sync) : "Mai";
                        
                        // Estrai numero e unità dal tempo relativo per bold separato
                        const syncTimeParts = lastSyncTime.match(/^(\d+)\s*(.+)$/);
                        const syncTimeNumber = syncTimeParts ? syncTimeParts[1] : null;
                        const syncTimeUnit = syncTimeParts ? syncTimeParts[2] : lastSyncTime;
                        
                        return (
                          <div 
                            key={idx} 
                            className={`px-6 py-5 rounded-lg border ${rowClass}`}
                          >
                            <div className="flex items-center text-[16px]">
                              {/* Nome piattaforma */}
                              <div className="flex-none w-[120px]">
                                <span className="font-bold text-[#1C1917]">{platform.platform_name}</span>
                              </div>
                              
                              {/* Separatore */}
                              <span className="text-[#C4C0BC] mx-4">|</span>
                              
                              {/* Stato */}
                              <div className="flex-none w-[100px] text-center">
                                <span className={`font-bold ${statusBadge.class}`}>{statusBadge.text}</span>
                              </div>
                              
                              {/* Separatore */}
                              <span className="text-[#C4C0BC] mx-4">|</span>
                              
                              {/* Prenotazioni */}
                              <div className="flex-none w-[140px] text-center">
                                <span className="text-[#44403C]">
                                  <span className="font-bold">{bookingsCount}</span> prenotazioni
                                </span>
                              </div>
                              
                              {/* Separatore */}
                              <span className="text-[#C4C0BC] mx-4">|</span>
                              
                              {/* Copertura calendario */}
                              <div className="flex-1 text-center">
                                <span className="text-[#44403C]" title="Data massima coperta dalle prenotazioni importate">
                                  Calendario sincronizzato fino al <span className="font-bold">{coverageDate || '—'}</span>
                                </span>
                              </div>
                              
                              {/* Separatore */}
                              <span className="text-[#C4C0BC] mx-4">|</span>
                              
                              {/* Ultima sync */}
                              <div className="flex-none w-[180px] text-right">
                                <span className={`${syncWarningLevel === "critical" ? "text-red-600" : syncWarningLevel === "warning" ? "text-orange-600" : "text-[#78716C]"}`}>
                                  Ultima sync: {syncTimeNumber ? (
                                    <><span className="font-bold">{syncTimeNumber}</span> {syncTimeUnit}</>
                                  ) : (
                                    <span className="font-bold">{lastSyncTime}</span>
                                  )}
                                </span>
                              </div>
                              
                              {/* Messaggio errore (se presente) */}
                              {platform.status === "error" && platform.status_message && (
                                <>
                                  <span className="text-[#C4C0BC] mx-4">|</span>
                                  <span className="text-red-600 text-sm">{platform.status_message}</span>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 p-4 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4] text-center">
                    <p className="text-[#78716C]">Nessuna piattaforma configurata.</p>
                    <p className="text-sm text-[#A8A29E] mt-1">
                      Puoi abilitare le piattaforme in{" "}
                      <button 
                        onClick={() => setActiveTab("source")}
                        className="text-[#D4AF37] hover:underline font-medium"
                      >
                        Data Source
                      </button>
                    </p>
                  </div>
                )}

                {/* Messaggio di stato globale */}
                {syncLogs.length > 0 && syncLogs[0].status !== 'error' && (
                  syncLogs[0].is_first_import && syncLogs[0].total_bookings > 0 ? (
                    <div className="p-3 bg-[#FFFBEB] border border-[#D4AF37] rounded-lg">
                      <p className="text-[#92400E] font-medium">
                        🆕 Prima importazione completata: {syncLogs[0].total_bookings} prenotazioni importate nell'ultima sincronizzazione
                      </p>
                    </div>
                  ) : syncLogs[0].new_bookings_count > 0 ? (
                    <div className="p-3 bg-[#FFFBEB] border border-[#D4AF37] rounded-lg">
                      <p className="text-[#92400E] font-medium">
                        🆕 {syncLogs[0].new_bookings_count} nuova/e prenotazione/i importata/e nell'ultima sincronizzazione
                      </p>
                    </div>
                  ) : syncLogs[0].delta > 0 ? (
                    <div className="p-3 bg-[#FFFBEB] border border-[#D4AF37] rounded-lg">
                      <p className="text-[#92400E] font-medium">
                        📊 +{syncLogs[0].delta} prenotazioni rilevate nell'ultima sincronizzazione
                      </p>
                    </div>
                  ) : syncLogs[0].delta < 0 ? (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 font-medium">
                        📊 {syncLogs[0].delta} prenotazioni rilevate nell'ultima sincronizzazione
                      </p>
                    </div>
                  ) : null
                )}

                {/* 6 CARD - Prenotazioni importate per periodo */}
                {importedBookingsStats && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" style={{marginTop: '26px'}}>
                    {/* CARD 1 - Oggi */}
                    <div className="flex flex-col bg-white border border-[#D4AF37] rounded-lg overflow-hidden h-[280px]">
                      <div className="bg-[#D4AF37] text-white px-3 py-2">
                        <p className="text-sm font-bold uppercase tracking-wide">Oggi</p>
                        <p className="text-xl font-bold">{importedBookingsStats.today?.count || 0}</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2">
                        {importedBookingsStats.today?.has_bookings ? (
                          <div className="space-y-1.5">
                            {importedBookingsStats.today.bookings.slice(0, 3).map((booking, idx) => (
                              <div key={idx} className="text-xs p-1.5 bg-[#FAFAF9] rounded border border-[#E7E5E4]">
                                <div className="flex justify-between">
                                  <span className="font-medium">{formatDisplayDate(booking.check_in)} → {formatDisplayDate(booking.check_out)}</span>
                                  <span className="text-[#78716C]">{booking.nights}n</span>
                                </div>
                                <p className="text-[#78716C] truncate text-[10px]">{booking.platform}</p>
                              </div>
                            ))}
                            {importedBookingsStats.today.count > 3 && (
                              <p className="text-[10px] text-[#78716C] text-center">+{importedBookingsStats.today.count - 3} altre</p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-center px-2">
                            <p className="text-xs text-[#78716C]">Nessuna prenotazione importata oggi</p>
                          </div>
                        )}
                      </div>
                      {importedBookingsStats.today?.has_bookings && (
                        <div className="border-t border-[#E7E5E4] p-2 bg-white shrink-0">
                          <button 
                            onClick={() => setExpandedCardBookings(expandedCardBookings === 'today' ? null : 'today')}
                            className="text-sm text-[#D4AF37] hover:text-[#B8972E] font-bold w-full text-center py-1"
                          >
                            Vedi elenco ({importedBookingsStats.today.count})
                          </button>
                        </div>
                      )}
                    </div>

                    {/* CARD 2 - Ultimi 3 GG */}
                    <div className="flex flex-col bg-white border border-[#D4AF37] rounded-lg overflow-hidden h-[280px]">
                      <div className="bg-[#D4AF37] text-white px-3 py-2">
                        <p className="text-sm font-bold uppercase tracking-wide">ULTIMI 3 GG</p>
                        <p className="text-xl font-bold">{importedBookingsStats.three_days?.count || 0}</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2">
                        {importedBookingsStats.three_days?.has_bookings ? (
                          <div className="space-y-1.5">
                            {importedBookingsStats.three_days.bookings.slice(0, 3).map((booking, idx) => (
                              <div key={idx} className="text-xs p-1.5 bg-[#FAFAF9] rounded border border-[#E7E5E4]">
                                <div className="flex justify-between">
                                  <span className="font-medium">{formatDisplayDate(booking.check_in)} → {formatDisplayDate(booking.check_out)}</span>
                                  <span className="text-[#78716C]">{booking.nights}n</span>
                                </div>
                                <p className="text-[#78716C] truncate text-[10px]">{booking.platform}</p>
                              </div>
                            ))}
                            {importedBookingsStats.three_days.count > 3 && (
                              <p className="text-[10px] text-[#78716C] text-center">+{importedBookingsStats.three_days.count - 3} altre</p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-center px-2">
                            <p className="text-xs text-[#78716C]">Nessuna prenotazione negli ultimi 3 GG</p>
                          </div>
                        )}
                      </div>
                      {importedBookingsStats.three_days?.has_bookings && (
                        <div className="border-t border-[#E7E5E4] p-2 bg-white shrink-0">
                          <button 
                            onClick={() => setExpandedCardBookings(expandedCardBookings === 'three_days' ? null : 'three_days')}
                            className="text-sm text-[#D4AF37] hover:text-[#B8972E] font-bold w-full text-center py-1"
                          >
                            Vedi elenco ({importedBookingsStats.three_days.count})
                          </button>
                        </div>
                      )}
                    </div>

                    {/* CARD 3 - 7 GG */}
                    <div className="flex flex-col bg-white border border-[#D4AF37] rounded-lg overflow-hidden h-[280px]">
                      <div className="bg-[#D4AF37] text-white px-3 py-2">
                        <p className="text-sm font-bold uppercase tracking-wide">ULTIMI 7 GG</p>
                        <p className="text-xl font-bold">{importedBookingsStats.seven_days?.count || 0}</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2">
                        {importedBookingsStats.seven_days?.has_bookings ? (
                          <div className="space-y-1.5">
                            {importedBookingsStats.seven_days.bookings.slice(0, 3).map((booking, idx) => (
                              <div key={idx} className="text-xs p-1.5 bg-[#FAFAF9] rounded border border-[#E7E5E4]">
                                <div className="flex justify-between">
                                  <span className="font-medium">{formatDisplayDate(booking.check_in)} → {formatDisplayDate(booking.check_out)}</span>
                                  <span className="text-[#78716C]">{booking.nights}n</span>
                                </div>
                                <p className="text-[#78716C] truncate text-[10px]">{booking.platform}</p>
                              </div>
                            ))}
                            {importedBookingsStats.seven_days.count > 3 && (
                              <p className="text-[10px] text-[#78716C] text-center">+{importedBookingsStats.seven_days.count - 3} altre</p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-center px-2">
                            <p className="text-xs text-[#78716C]">Nessuna prenotazione negli ultimi 7 GG</p>
                          </div>
                        )}
                      </div>
                      {importedBookingsStats.seven_days?.has_bookings && (
                        <div className="border-t border-[#E7E5E4] p-2 bg-white shrink-0">
                          <button 
                            onClick={() => setExpandedCardBookings(expandedCardBookings === 'seven_days' ? null : 'seven_days')}
                            className="text-sm text-[#D4AF37] hover:text-[#B8972E] font-bold w-full text-center py-1"
                          >
                            Vedi elenco ({importedBookingsStats.seven_days.count})
                          </button>
                        </div>
                      )}
                    </div>

                    {/* CARD 4 - 15 GG */}
                    <div className="flex flex-col bg-white border border-[#D4AF37] rounded-lg overflow-hidden h-[280px]">
                      <div className="bg-[#D4AF37] text-white px-3 py-2">
                        <p className="text-sm font-bold uppercase tracking-wide">ULTIMI 15 GG</p>
                        <p className="text-xl font-bold">{importedBookingsStats.fifteen_days?.count || 0}</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2">
                        {importedBookingsStats.fifteen_days?.has_bookings ? (
                          <div className="space-y-1.5">
                            {importedBookingsStats.fifteen_days.bookings.slice(0, 3).map((booking, idx) => (
                              <div key={idx} className="text-xs p-1.5 bg-[#FAFAF9] rounded border border-[#E7E5E4]">
                                <div className="flex justify-between">
                                  <span className="font-medium">{formatDisplayDate(booking.check_in)} → {formatDisplayDate(booking.check_out)}</span>
                                  <span className="text-[#78716C]">{booking.nights}n</span>
                                </div>
                                <p className="text-[#78716C] truncate text-[10px]">{booking.platform}</p>
                              </div>
                            ))}
                            {importedBookingsStats.fifteen_days.count > 3 && (
                              <p className="text-[10px] text-[#78716C] text-center">+{importedBookingsStats.fifteen_days.count - 3} altre</p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-center px-2">
                            <p className="text-xs text-[#78716C]">Nessuna prenotazione negli ultimi 15 GG</p>
                          </div>
                        )}
                      </div>
                      {importedBookingsStats.fifteen_days?.has_bookings && (
                        <div className="border-t border-[#E7E5E4] p-2 bg-white shrink-0">
                          <button 
                            onClick={() => setExpandedCardBookings(expandedCardBookings === 'fifteen_days' ? null : 'fifteen_days')}
                            className="text-sm text-[#D4AF37] hover:text-[#B8972E] font-bold w-full text-center py-1"
                          >
                            Vedi elenco ({importedBookingsStats.fifteen_days.count})
                          </button>
                        </div>
                      )}
                    </div>

                    {/* CARD 5 - 30 GG */}
                    <div className="flex flex-col bg-white border border-[#D4AF37] rounded-lg overflow-hidden h-[280px]">
                      <div className="bg-[#D4AF37] text-white px-3 py-2">
                        <p className="text-sm font-bold uppercase tracking-wide">ULTIMI 30 GG</p>
                        <p className="text-xl font-bold">{importedBookingsStats.thirty_days?.count || 0}</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2">
                        {importedBookingsStats.thirty_days?.has_bookings ? (
                          <div className="space-y-1.5">
                            {importedBookingsStats.thirty_days.bookings.slice(0, 3).map((booking, idx) => (
                              <div key={idx} className="text-xs p-1.5 bg-[#FAFAF9] rounded border border-[#E7E5E4]">
                                <div className="flex justify-between">
                                  <span className="font-medium">{formatDisplayDate(booking.check_in)} → {formatDisplayDate(booking.check_out)}</span>
                                  <span className="text-[#78716C]">{booking.nights}n</span>
                                </div>
                                <p className="text-[#78716C] truncate text-[10px]">{booking.platform}</p>
                              </div>
                            ))}
                            {importedBookingsStats.thirty_days.count > 3 && (
                              <p className="text-[10px] text-[#78716C] text-center">+{importedBookingsStats.thirty_days.count - 3} altre</p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-center px-2">
                            <p className="text-xs text-[#78716C]">Nessuna prenotazione negli ultimi 30 GG</p>
                          </div>
                        )}
                      </div>
                      {importedBookingsStats.thirty_days?.has_bookings && (
                        <div className="border-t border-[#E7E5E4] p-2 bg-white shrink-0">
                          <button 
                            onClick={() => setExpandedCardBookings(expandedCardBookings === 'thirty_days' ? null : 'thirty_days')}
                            className="text-sm text-[#D4AF37] hover:text-[#B8972E] font-bold w-full text-center py-1"
                          >
                            Vedi elenco ({importedBookingsStats.thirty_days.count})
                          </button>
                        </div>
                      )}
                    </div>

                    {/* CARD 6 - Totale */}
                    <div className="flex flex-col bg-white border border-[#D4AF37] rounded-lg overflow-hidden h-[280px]">
                      <div className="bg-[#D4AF37] text-white px-3 py-2">
                        <p className="text-sm font-bold uppercase tracking-wide">TOTALE</p>
                        <p className="text-xl font-bold">{importedBookingsStats.total?.count || 0}</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2">
                        {importedBookingsStats.total?.has_bookings ? (
                          <div className="space-y-1.5">
                            {importedBookingsStats.total.bookings.slice(0, 3).map((booking, idx) => (
                              <div key={idx} className="text-xs p-1.5 bg-[#FAFAF9] rounded border border-[#E7E5E4]">
                                <div className="flex justify-between">
                                  <span className="font-medium">{formatDisplayDate(booking.check_in)} → {formatDisplayDate(booking.check_out)}</span>
                                  <span className="text-[#78716C]">{booking.nights}n</span>
                                </div>
                                <p className="text-[#78716C] truncate text-[10px]">{booking.platform}</p>
                              </div>
                            ))}
                            {importedBookingsStats.total.count > 3 && (
                              <p className="text-[10px] text-[#78716C] text-center">+{importedBookingsStats.total.count - 3} altre</p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-center px-2">
                            <p className="text-xs text-[#78716C]">Nessuna prenotazione importata</p>
                          </div>
                        )}
                      </div>
                      {importedBookingsStats.total?.has_bookings && (
                        <div className="border-t border-[#E7E5E4] p-2 bg-white shrink-0">
                          <button 
                            onClick={() => setExpandedCardBookings(expandedCardBookings === 'total' ? null : 'total')}
                            className="text-sm text-[#D4AF37] hover:text-[#B8972E] font-bold w-full text-center py-1"
                          >
                            Vedi elenco ({importedBookingsStats.total.count})
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  </>
                )}

                {/* Expanded bookings list - Modal overlay for mobile */}
                {expandedCardBookings && importedBookingsStats?.[expandedCardBookings]?.all_bookings && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setExpandedCardBookings(null)}>
                    <div 
                      className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between p-4 border-b border-[#E7E5E4]">
                        <h4 className="font-semibold text-[#1C1917]">
                          Elenco prenotazioni - {importedBookingsStats[expandedCardBookings].period}
                          <span className="ml-2 text-sm font-normal text-[#78716C]">
                            ({importedBookingsStats[expandedCardBookings].all_bookings.length} totali)
                          </span>
                        </h4>
                        <button 
                          onClick={() => setExpandedCardBookings(null)}
                          className="text-[#78716C] hover:text-[#1C1917] p-2"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4">
                        {/* Mobile: card list */}
                        <div className="block sm:hidden space-y-2">
                          {importedBookingsStats[expandedCardBookings].all_bookings.map((booking, idx) => (
                            <div key={idx} className="p-3 bg-[#FAFAF9] rounded border border-[#E7E5E4]">
                              <div className="flex justify-between items-start mb-1">
                                <span className="text-xs text-[#78716C]">#{idx + 1}</span>
                                <span className="text-xs text-[#78716C]">
                                  {formatDisplayDateTime(booking.imported_at)}
                                </span>
                              </div>
                              <div className="font-medium text-sm">
                                {formatDisplayDate(booking.check_in)} → {formatDisplayDate(booking.check_out)}
                              </div>
                              <div className="flex justify-between text-xs text-[#78716C] mt-1">
                                <span>{booking.nights} notti</span>
                                <span>{booking.platform}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Desktop: table */}
                        <div className="hidden sm:block">
                          <table className="w-full text-sm">
                            <thead className="bg-[#FAFAF9] sticky top-0">
                              <tr>
                                <th className="text-left px-3 py-2 text-[#78716C] font-medium">#</th>
                                <th className="text-left px-3 py-2 text-[#78716C] font-medium">Check-in</th>
                                <th className="text-left px-3 py-2 text-[#78716C] font-medium">Check-out</th>
                                <th className="text-left px-3 py-2 text-[#78716C] font-medium">Notti</th>
                                <th className="text-left px-3 py-2 text-[#78716C] font-medium">Piattaforma</th>
                                <th className="text-left px-3 py-2 text-[#78716C] font-medium">Importata il</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E7E5E4]">
                              {importedBookingsStats[expandedCardBookings].all_bookings.map((booking, idx) => (
                                <tr key={idx} className="hover:bg-[#FAFAF9]">
                                  <td className="px-3 py-2 text-[#78716C]">{idx + 1}</td>
                                  <td className="px-3 py-2 font-medium">{formatDisplayDate(booking.check_in)}</td>
                                  <td className="px-3 py-2 font-medium">{formatDisplayDate(booking.check_out)}</td>
                                  <td className="px-3 py-2">{booking.nights}</td>
                                  <td className="px-3 py-2">{booking.platform}</td>
                                  <td className="px-3 py-2 text-xs text-[#78716C]">
                                    {formatDisplayDateTime(booking.imported_at)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-[#78716C]">
                <History size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nessuna sincronizzazione ancora eseguita</p>
              </div>
            )}
          </div>

          {/* 1. RIQUADRO: Ultime Prenotazioni Rilevate da iCal */}
          <div className="admin-card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl">Ultime Prenotazioni Rilevate da iCal</h3>
              <div className="flex items-center gap-3">
                {/* Elimina selezionati - prenotazioni (solo se ci sono selezioni) */}
                {selectedBookingLogs.length > 0 && (
                  <Button
                    onClick={() => {
                      setDeleteTargetType("bookings");
                      setShowSimpleDeleteModal(true);
                    }}
                    variant="outline"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    size="sm"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Elimina ({selectedBookingLogs.length})
                  </Button>
                )}
                {/* Elimina tutto - prenotazioni (sempre visibile se ci sono prenotazioni) */}
                {syncLogs.filter(log => log.new_bookings_count > 0 || log.delta > 0).length > 0 && selectedBookingLogs.length === 0 && (
                  <Button
                    onClick={() => {
                      setDeleteTargetType("all_bookings");
                      setShowSimpleDeleteModal(true);
                    }}
                    variant="outline"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    size="sm"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Elimina tutto
                  </Button>
                )}
                <PageInfoIcon pageKey="ultime_prenotazioni_rilevate" />
              </div>
            </div>
            
            {/* Lista prenotazioni rilevate (solo eventi con prenotazioni importate) - DETTAGLIO SEMPRE VISIBILE */}
            {(() => {
              const bookingLogs = syncLogs.filter(log => log.new_bookings_count > 0 || log.delta > 0);
              
              if (bookingLogs.length === 0) {
                return (
                  <div className="text-center py-8 flex flex-col items-center">
                    <span className="text-green-600 mb-3">
                      <Check size={24} strokeWidth={2.5} />
                    </span>
                    <p className="text-[#78716C] font-medium">Nessuna nuova Prenotazione</p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-4">
                  {bookingLogs.slice(0, 10).map((log) => (
                    <div 
                      key={log.log_id} 
                      className={`border rounded-lg overflow-hidden ${selectedBookingLogs.includes(log.log_id) ? 'border-red-400 bg-red-50/30' : 'border-[#D4AF37] bg-[#FFFDF7]'}`}
                    >
                      <div className="p-4">
                        {/* Header: Checkbox + Badge + Testo + Timestamp sulla stessa riga */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedBookingLogs.includes(log.log_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedBookingLogs([...selectedBookingLogs, log.log_id]);
                                } else {
                                  setSelectedBookingLogs(selectedBookingLogs.filter(id => id !== log.log_id));
                                }
                              }}
                              className="w-4 h-4 accent-[#D4AF37]"
                            />
                            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center shrink-0">
                              <span className="text-white font-bold">
                                +{log.new_bookings_count || log.delta || 0}
                              </span>
                            </div>
                            <p className="font-semibold text-[#1C1917]">
                              {(log.new_bookings_count || log.delta || 0) === 1 
                                ? "1 nuova prenotazione" 
                                : `${log.new_bookings_count || log.delta || 0} nuove prenotazioni`}
                            </p>
                          </div>
                          {/* Timestamp sulla stessa riga, allineato a destra */}
                          <div className="text-right shrink-0">
                            <span className="text-sm font-medium text-[#1C1917]">{formatDisplayDateTime(log.timestamp)}</span>
                            <span className="text-xs text-[#78716C] ml-2">{formatRelativeTime(log.timestamp)}</span>
                          </div>
                        </div>

                        {/* Dettaglio Prenotazioni - SEMPRE VISIBILE */}
                        {log.new_bookings && log.new_bookings.length > 0 && (
                          <div className="border-t border-[#D4AF37]/30 pt-4">
                            <div className="grid gap-2">
                              {log.new_bookings.map((booking, idx) => (
                                <div key={idx} className="bg-white p-3 rounded-lg border border-[#E7E5E4]">
                                  <div className="flex justify-between items-start text-sm w-full">
                                    <div className="text-left">
                                      <span className="text-[#A8A29E] text-xs uppercase block">Check-in</span>
                                      <p className="font-semibold text-[#1C1917]">{formatDisplayDate(booking.check_in)}</p>
                                    </div>
                                    <div className="text-left">
                                      <span className="text-[#A8A29E] text-xs uppercase block">Check-out</span>
                                      <p className="font-semibold text-[#1C1917]">{formatDisplayDate(booking.check_out)}</p>
                                    </div>
                                    <div className="text-left">
                                      <span className="text-[#A8A29E] text-xs uppercase block">Notti</span>
                                      <p className="font-semibold text-[#1C1917]">{booking.nights}</p>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-[#A8A29E] text-xs uppercase block">Piattaforma</span>
                                      <p className="font-semibold text-[#1C1917]">{booking.platform || log.platform_name}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Nota retention per prenotazioni rilevate - CENTRATA */}
                  <p className="text-xs text-[#A8A29E] mt-4 text-center">
                    Le prenotazioni vengono eliminate automaticamente dopo 90 giorni.
                  </p>
                </div>
              );
            })()}
          </div>

          {/* 2. RIQUADRO: Log - SOLO errori di sincronizzazione */}
          {(() => {
            // Filtra SOLO errori - i sync OK senza prenotazioni NON vengono mostrati
            const errorLogs = syncLogs.filter(log => log.status === 'error');
            
            // Applica filtri locali per la sezione Log (separati dalla sezione Prenotazioni)
            let filteredErrorLogs = [...errorLogs];
            
            // Filtro piattaforma (solo per log errori)
            if (syncFilterPlatform !== "all") {
              filteredErrorLogs = filteredErrorLogs.filter(log => log.platform_name === syncFilterPlatform);
            }
            
            // Filtro periodo (solo per log errori) 
            if (syncFilterPeriod !== "all") {
              const now = new Date();
              let cutoff;
              if (syncFilterPeriod === "today") {
                cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              } else if (syncFilterPeriod === "24h") {
                cutoff = new Date(now - 24 * 60 * 60 * 1000);
              } else if (syncFilterPeriod === "7d") {
                cutoff = new Date(now - 7 * 24 * 60 * 60 * 1000);
              } else if (syncFilterPeriod === "30d") {
                cutoff = new Date(now - 30 * 24 * 60 * 60 * 1000);
              }
              if (cutoff) {
                filteredErrorLogs = filteredErrorLogs.filter(log => new Date(log.timestamp) >= cutoff);
              }
            }
            
            return (
              <div className="admin-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl">Log da iCal</h3>
                  <div className="flex items-center gap-3">
                    {/* Pulsante Elimina selezionati - solo se ci sono selezioni */}
                    {selectedSyncLogs.length > 0 && (
                      <Button
                        onClick={() => {
                          setDeleteTargetType("logs");
                          setShowSimpleDeleteModal(true);
                        }}
                        variant="outline"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                        size="sm"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Elimina ({selectedSyncLogs.length})
                      </Button>
                    )}
                    {/* Pulsante Elimina tutto - SOLO se ci sono errori e nessuna selezione */}
                    {filteredErrorLogs.length > 0 && selectedSyncLogs.length === 0 && (
                      <Button
                        onClick={() => {
                          setDeleteTargetType("all_logs");
                          setShowSimpleDeleteModal(true);
                        }}
                        variant="outline"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                        size="sm"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Elimina tutto
                      </Button>
                    )}
                    <PageInfoIcon pageKey="log_tecnici" />
                  </div>
                </div>

                {/* Filtri per log - mostrati solo se ci sono errori */}
                {errorLogs.length > 0 && (
                  <div className="flex flex-wrap gap-3 p-3 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4] mb-4">
                    {/* Filtro Piattaforma */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-[#78716C]">Piattaforma:</label>
                      <select
                        value={syncFilterPlatform}
                        onChange={(e) => setSyncFilterPlatform(e.target.value)}
                        className="px-3 py-1.5 border border-[#D6D3D1] rounded-md text-sm bg-white"
                      >
                        <option value="all">Tutte</option>
                        {availablePlatforms.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>

                    {/* Filtro Periodo */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-[#78716C]">Periodo:</label>
                      <select
                        value={syncFilterPeriod}
                        onChange={(e) => setSyncFilterPeriod(e.target.value)}
                        className="px-3 py-1.5 border border-[#D6D3D1] rounded-md text-sm bg-white"
                      >
                        <option value="all">Tutti</option>
                        <option value="today">Oggi</option>
                        <option value="24h">Ultime 24 ore</option>
                        <option value="7d">Ultimi 7 giorni</option>
                        <option value="30d">Ultimi 30 giorni</option>
                        <option value="90d">Ultimi 90 giorni</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Lista errori */}
                <div className="space-y-3">
                  {filteredErrorLogs.length > 0 ? (
                    <div className="space-y-2">
                      {/* Banner errori */}
                      <div className="p-3 bg-red-100 border-2 border-red-400 rounded-lg flex items-center gap-3">
                        <AlertCircle size={24} className="text-red-600" />
                        <div>
                          <p className="font-bold text-red-800">
                            ⚠️ {filteredErrorLogs.length} errore/i di sincronizzazione
                          </p>
                          <p className="text-sm text-red-600">Richiede attenzione immediata</p>
                        </div>
                      </div>
                      {/* Lista singoli errori */}
                      {filteredErrorLogs.map((log) => (
                        <div key={log.log_id} className="p-4 bg-red-50 border border-red-300 rounded-lg">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={selectedSyncLogs.includes(log.log_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedSyncLogs([...selectedSyncLogs, log.log_id]);
                                  } else {
                                    setSelectedSyncLogs(selectedSyncLogs.filter(id => id !== log.log_id));
                                  }
                                }}
                                className="rounded border-red-300 w-4 h-4 mt-1"
                              />
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-0.5 bg-red-200 text-red-800 text-xs font-bold rounded">ERRORE</span>
                                  <span className="font-semibold text-red-900">{log.platform_name}</span>
                                </div>
                                <p className="text-sm text-red-700 mt-1">{log.message || "Errore durante la sincronizzazione iCal"}</p>
                                <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                                  <AlertCircle size={12} />
                                  Azione: Verificare URL iCal e connessione
                                </p>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xs font-medium text-red-800">{formatDisplayDateTime(log.timestamp)}</p>
                              <p className="text-[10px] text-red-500">{formatRelativeTime(log.timestamp)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Stato OK - Nessun errore */
                    <div className="text-center py-8 flex flex-col items-center">
                      <span className="text-green-600 mb-3">
                        <Check size={24} strokeWidth={2.5} />
                      </span>
                      <p className="text-[#78716C] font-medium">Nessun errore di sincronizzazione</p>
                    </div>
                  )}
                </div>

                {/* Nota retention - SEMPRE visibile e CENTRATA */}
                <p className="text-xs text-[#A8A29E] mt-4 text-center">
                  I log vengono eliminati automaticamente dopo 90 giorni.
                </p>
              </div>
            );
          })()}

        </TabsContent>
      </Tabs>
      
      {/* Delete Price Rule Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deletePriceDialogOpen}
        onOpenChange={setDeletePriceDialogOpen}
        title={deletingPriceBulk ? "Elimina Regole Prezzo Selezionate" : "Elimina Regola Prezzo"}
        description={deletingPriceBulk 
          ? `Stai per eliminare ${selectedPrices.length} regole di prezzo.`
          : "Stai per eliminare questa regola di prezzo."
        }
        itemCount={deletingPriceBulk ? `${selectedPrices.length} regole selezionate` : null}
        onConfirm={confirmDeletePrice}
      />
      
      {/* Delete Platform Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deletePlatformDialogOpen}
        onOpenChange={setDeletePlatformDialogOpen}
        title="Elimina Piattaforma iCal"
        description={`Stai per eliminare la piattaforma "${deletingPlatform?.platform_name || ''}". Anche le date bloccate associate verranno rimosse.`}
        onConfirm={confirmDeletePlatform}
      />
      
      {/* Delete Booking Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteBookingDialogOpen}
        onOpenChange={setDeleteBookingDialogOpen}
        title="Elimina Prenotazione"
        description="Stai per eliminare questa prenotazione."
        onConfirm={confirmDeleteBooking}
      />
      
      {/* Generic Confirmation Modal (non-destructive) */}
      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={confirmModalConfig.onConfirm}
        title={confirmModalConfig.title}
        message={confirmModalConfig.message}
        variant={confirmModalConfig.variant}
        confirmText="Conferma"
        cancelText="Annulla"
      />
      
      {/* Delete Bookings from Calendar Modal */}
      <ConfirmModal
        isOpen={deleteBookingModalOpen}
        onClose={() => {
          setDeleteBookingModalOpen(false);
          setBookingsToDelete([]);
        }}
        onConfirm={async () => {
          try {
            for (const requestId of bookingsToDelete) {
              await axios.delete(`${API}/admin/bookings/${requestId}`, getAuthHeaders());
            }
            toast.success(`${bookingsToDelete.length} prenotazion${bookingsToDelete.length === 1 ? 'e eliminata' : 'i eliminate'}`);
            fetchData();
          } catch (error) {
            console.error('Delete error:', error);
            toast.error("Errore nell'eliminazione");
          } finally {
            setDeleteBookingModalOpen(false);
            setBookingsToDelete([]);
          }
        }}
        title="Elimina Prenotazioni"
        message={`Sei sicuro di voler eliminare ${bookingsToDelete.length} prenotazion${bookingsToDelete.length === 1 ? 'e' : 'i'}? Le date verranno liberate.`}
        variant="danger"
        confirmText="Elimina"
        cancelText="Annulla"
      />
    </div>
  );
}
